import { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash, Check, FileText, FingerprintSimple, CaretUp, CaretDown, DotsSixVertical, Palette, Copy, Eye, ArrowsClockwise, ArrowLineDown } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { DiseaseGroupsManager } from './DiseaseGroupsManager';
import ESignature from '../../components/ESignature';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import { useAuth } from '../../contexts/AuthContext';
import { RichTextField } from './RichTextField';
import { PhaseClosurePanel } from './PhaseClosurePanel';
import { PublicValidationTokensPanel } from './PublicValidationTokensPanel';
import { CustomTableDesigner } from '../CustomTable';
// URS-F497: drag-and-drop para reordenar preguntas en Config -> Cuestionarios.
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// URS-F497: fila draggable reutilizable dentro del editor de cuestionarios
// de configuracion. Estilo alineado con `SortableQuestionRow` de
// `components/study/StudyPanels.js` (URS-F316) para mantener UX consistente.
function ConfigSortableRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} data-testid={`config-q-sort-row-${id}`}>
      {children({ attributes, listeners, isDragging })}
    </div>
  );
}
import { CompositeScaleDesigner } from '../CompositeScale';
import { QuestionnairePreviewDialog } from '../study/QuestionnairePreviewDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const roleLabels = { admin: 'Admin', investigador_principal: 'Investigador Principal', investigador: 'Co-Investigador', director_estudio: 'Director Estudio', monitor: 'Monitor', coordinador: 'Coordinador', data_manager: 'Data Manager', sondas: 'Sondas', supervisor_sondas: 'Supervisor Sondas', reclutamiento: 'Reclutamiento', supervisor_reclutamiento: 'Supervisor Reclutamiento', odontologia: 'Odontología' };
const roleOptions = { admin: 'Admin', investigador_principal: 'Investigador Principal', investigador: 'Co-Investigador', director_estudio: 'Director Estudio', monitor: 'Monitor', coordinador: 'Coordinador', data_manager: 'Data Manager', sondas: 'Sondas', supervisor_sondas: 'Supervisor Sondas', reclutamiento: 'Reclutamiento', supervisor_reclutamiento: 'Supervisor Reclutamiento', odontologia: 'Odontología' };

const questionTypeLabels = {
  es: { text: 'Texto', number: 'Numero', date: 'Fecha', date_ongoing: 'Fecha (con opcion "continua")', scale: 'Escala', select: 'Selector', multiselect: 'Multiseleccion', boolean: 'Si/No', boolean_with_obs: 'Si/No + Observacion', textarea: 'Area de texto', derm_scale: 'Escala dermatológica (con imagen)', masi: 'Escala MASI (Melasma) - compuesta', composite_scale: 'Escala compuesta (sub-escalas con suma)', likert_1_5: 'Satisfacción (Likert 1-5)', likert_1_7: 'Satisfacción (Likert 1-7)', table_text: 'Tabla (opciones + texto)', table_select: 'Tabla (opciones + selector)', custom_table: 'Tabla personalizada (NxM)', nps: 'NPS (0-10)', nps_multi: 'NPS múltiple (varios sub-apartados)', checklist: 'Lista de verificación (checkboxes)' },
  en: { text: 'Text', number: 'Number', date: 'Date', date_ongoing: 'Date (with "ongoing" option)', scale: 'Scale', select: 'Select', multiselect: 'Multi-select', boolean: 'Yes/No', boolean_with_obs: 'Yes/No + Observation', textarea: 'Textarea', derm_scale: 'Dermatological scale (with image)', masi: 'MASI score (Melasma) - composite', composite_scale: 'Composite scale (sub-scales with sum)', likert_1_5: 'Satisfaction (Likert 1-5)', likert_1_7: 'Satisfaction (Likert 1-7)', table_text: 'Table (options + text)', table_select: 'Table (options + select)', custom_table: 'Custom table (NxM)', nps: 'NPS (0-10)', nps_multi: 'Multi NPS (several sub-items)', checklist: 'Checklist (checkboxes)' },
};

const defaultCategories = ['tolerancia', 'eficacia', 'seguridad', 'dermatologia', 'general'];

import UserScheduleBlocksSection from '../UserScheduleBlocksSection';


export function UsersTab({ lang }) {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [clientsMap, setClientsMap] = useState({});
  const [viewMode, setViewMode] = useState('users'); // 'users' | 'clients'
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ name: '', surname1: '', surname2: '', initials: '', email: '', password: '', roles: [], is_professional: false, specialty: '', permission_group_ids: [], phone: '', dni: '', pay_per_visit_enabled: false, pay_per_visit_amount: '', is_active: true });
  // URS-F300: defensa contra autofill del navegador en el campo password al editar
  // un usuario. El input solo se muestra si el admin pulsa "Cambiar contrasena"
  // (showPasswordField) y SOLO se envia al backend si lo toco manualmente
  // (passwordTouched). Sin esto, Chrome rellena el password con la contrasena
  // guardada del admin y al guardar se reescribe el password_hash del usuario.
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [permGroups, setPermGroups] = useState([]);
  // URS-F277: estado de bloqueo por intentos fallidos del usuario abierto.
  const [lockStatus, setLockStatus] = useState(null);
  const [unlocking, setUnlocking] = useState(false);

  const fetchLockStatus = async (userId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/${userId}/lock-status`, { withCredentials: true });
      setLockStatus(data);
    } catch { setLockStatus(null); }
  };
  const handleUnlock = async () => {
    if (!editingUser) return;
    setUnlocking(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/users/${editingUser.id}/unlock`, {}, { withCredentials: true });
      toast.success(lang === 'es' ? `Usuario desbloqueado (${data.deleted_attempts} intentos limpiados)` : `User unlocked (${data.deleted_attempts} attempts cleared)`);
      await fetchLockStatus(editingUser.id);
    } catch (e) {
      toast.error(errText(e, lang === 'es' ? 'Error al desbloquear' : 'Error unlocking'));
    } finally {
      setUnlocking(false);
    }
  };

  useEffect(() => { fetchUsers(); fetchPermGroups(); fetchClients(); }, []);
  const fetchUsers = async () => { try { const { data } = await axios.get(`${API_URL}/api/users`, { withCredentials: true }); const sorted = (data || []).slice().sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || '', 'es', { sensitivity: 'base' })); setUsers(sorted); } catch {} };
  const fetchPermGroups = async () => { try { const { data } = await axios.get(`${API_URL}/api/permissions/groups`, { withCredentials: true }); setPermGroups(data || []); } catch {} };
  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/clients?page=1&limit=500`, { withCredentials: true });
      const items = Array.isArray(data) ? data : (data.items || []);
      const map = {};
      items.forEach(c => { if (c.client_id) map[c.client_id] = c.name || c.client_id; });
      setClientsMap(map);
    } catch {}
  };

  const isPortalClient = (u) => {
    const roles = u.roles || (u.role ? [u.role] : []);
    return roles.includes('cliente') || !!u.portal_access || !!u.client_id;
  };
  const filteredUsers = users.filter(u => viewMode === 'clients' ? isPortalClient(u) : !isPortalClient(u));

  const openCreate = () => {
    setEditingUser(null);
    setForm({ name: '', surname1: '', surname2: '', initials: '', email: '', password: '', roles: [], is_professional: false, specialty: '', permission_group_ids: [], phone: '', dni: '', pay_per_visit_enabled: false, pay_per_visit_amount: '', is_active: true });
    setShowPasswordField(true);   // al crear, el campo SIEMPRE es visible y obligatorio
    setPasswordTouched(false);
    setShowDialog(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    const roles = u.roles || (u.role ? [u.role] : []);
    setForm({ name: u.name, surname1: u.surname1 || '', surname2: u.surname2 || '', initials: u.initials || '', email: u.email, password: '', roles, is_professional: u.is_professional || false, specialty: u.specialty || '', permission_group_ids: u.permission_group_ids || [], phone: u.phone || '', dni: u.dni || '', pay_per_visit_enabled: !!u.pay_per_visit_enabled, pay_per_visit_amount: (u.pay_per_visit_amount ?? '') === null ? '' : (u.pay_per_visit_amount ?? ''), is_active: u.is_active !== false });
    setShowPasswordField(false);  // en edicion, oculto por defecto. Solo aparece con click.
    setPasswordTouched(false);
    setShowDialog(true);
    fetchLockStatus(u.id);
  };

  const toggleRole = (roleKey) => {
    setForm(p => ({
      ...p,
      roles: p.roles.includes(roleKey)
        ? p.roles.filter(r => r !== roleKey)
        : [...p.roles, roleKey]
    }));
  };

  const togglePermGroup = (gid) => {
    setForm(p => ({
      ...p,
      permission_group_ids: (p.permission_group_ids || []).includes(gid)
        ? p.permission_group_ids.filter(g => g !== gid)
        : [...(p.permission_group_ids || []), gid]
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(lang === 'es' ? 'El nombre es obligatorio' : 'Name is required');
      return;
    }
    if (!form.initials.trim()) {
      toast.error(lang === 'es' ? 'Las iniciales son obligatorias' : 'Initials are required');
      return;
    }
    if (!form.email.trim()) {
      toast.error(lang === 'es' ? 'El email es obligatorio' : 'Email is required');
      return;
    }
    if (form.is_professional && !form.specialty) {
      toast.error(lang === 'es' ? 'La especialidad es obligatoria para profesionales' : 'Specialty is required for professionals');
      return;
    }
    if (form.roles.length === 0) {
      toast.error(lang === 'es' ? 'Selecciona al menos un rol' : 'Select at least one role');
      return;
    }
    setSaving(true);
    // URS-F383: normalizar pago por visita. Si no es profesional o el switch
    // esta apagado, importe = null. Si esta activo, parsear a float valido.
    let payEnabled = form.is_professional && !!form.pay_per_visit_enabled;
    let payAmount = null;
    if (payEnabled) {
      const raw = String(form.pay_per_visit_amount ?? '').replace(',', '.').trim();
      if (raw === '') {
        toast.error(lang === 'es' ? 'Introduce el precio por visita' : 'Enter the price per visit');
        setSaving(false);
        return;
      }
      const parsed = parseFloat(raw);
      if (isNaN(parsed) || parsed < 0) {
        toast.error(lang === 'es' ? 'Precio por visita inválido' : 'Invalid price per visit');
        setSaving(false);
        return;
      }
      payAmount = parsed;
    }
    try {
      if (editingUser) {
        const payload = { name: form.name, surname1: form.surname1 || '', surname2: form.surname2 || '', initials: form.initials, email: form.email, roles: form.roles, is_professional: form.is_professional, specialty: form.specialty, permission_group_ids: form.permission_group_ids, phone: form.phone || '', dni: form.dni || '', pay_per_visit_enabled: payEnabled, pay_per_visit_amount: payAmount, is_active: !!form.is_active };
        // URS-F300: solo enviar password si el admin lo activo Y escribio algo.
        // Esto bloquea el autofill del navegador (el campo ni siquiera esta
        // montado a menos que showPasswordField=true).
        if (showPasswordField && passwordTouched && form.password && form.password.trim()) {
          payload.password = form.password;
        }
        await axios.put(`${API_URL}/api/users/${editingUser.id}`, payload, { withCredentials: true });
        toast.success(lang === 'es' ? 'Usuario actualizado' : 'User updated');
      } else {
        const createPayload = { ...form, pay_per_visit_enabled: payEnabled, pay_per_visit_amount: payAmount };
        await axios.post(`${API_URL}/api/users`, createPayload, { withCredentials: true });
        toast.success(lang === 'es' ? 'Usuario creado' : 'User created');
      }
      setShowDialog(false);
      setEditingUser(null);
      setForm({ name: '', surname1: '', surname2: '', initials: '', email: '', password: '', roles: [], is_professional: false, specialty: '', permission_group_ids: [], phone: '', dni: '', pay_per_visit_enabled: false, pay_per_visit_amount: '', is_active: true });
      fetchUsers();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const handleDelete = async (userId, signature) => {
    try { await axios.delete(`${API_URL}/api/users/${userId}`, { withCredentials: true, data: { signature } }); toast.success(lang === 'es' ? 'Usuario eliminado' : 'User deleted'); setDeleteTarget(null); fetchUsers(); }
    catch (err) { toast.error(errText(err)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex bg-stone-100 border border-stone-200 rounded-sm p-0.5" data-testid="users-view-tabs">
          <button
            type="button"
            data-testid="users-view-tab-users"
            onClick={() => setViewMode('users')}
            className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${viewMode === 'users' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
          >
            {lang === 'es' ? 'Usuarios' : 'Users'} <span className="ml-1 text-stone-400">({users.filter(u => !isPortalClient(u)).length})</span>
          </button>
          <button
            type="button"
            data-testid="users-view-tab-clients"
            onClick={() => setViewMode('clients')}
            className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${viewMode === 'clients' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
          >
            {lang === 'es' ? 'Clientes del portal' : 'Portal clients'} <span className="ml-1 text-stone-400">({users.filter(u => isPortalClient(u)).length})</span>
          </button>
        </div>
        {viewMode === 'users' && (
          <Button data-testid="create-user-btn" onClick={openCreate} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2"><Plus size={16} />{lang === 'es' ? 'Nuevo Usuario' : 'New User'}</Button>
        )}
      </div>
      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <Table className="dense-table">
          <TableHeader><TableRow>
            <TableHead>{lang === 'es' ? 'Nombre' : 'Name'}</TableHead>
            {viewMode === 'users' && <TableHead>{lang === 'es' ? 'Primer apellido' : 'Surname 1'}</TableHead>}
            {viewMode === 'users' && <TableHead>{lang === 'es' ? 'Iniciales' : 'Initials'}</TableHead>}
            <TableHead>Email</TableHead>
            {viewMode === 'clients'
              ? <TableHead>{lang === 'es' ? 'Cliente' : 'Client'}</TableHead>
              : <TableHead>{lang === 'es' ? 'Roles' : 'Roles'}</TableHead>}
            {viewMode === 'users' && <TableHead>{lang === 'es' ? 'Especialidad' : 'Specialty'}</TableHead>}
            <TableHead className="w-20"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={viewMode === 'clients' ? 4 : 7} className="text-center text-stone-400 py-8 text-sm">
                  {viewMode === 'clients'
                    ? (lang === 'es' ? 'No hay clientes con acceso al portal. Activa el acceso desde Clientes -> pestana "Acceso al portal".' : 'No portal clients yet. Activate portal access from Clients -> "Portal access" tab.')
                    : (lang === 'es' ? 'No hay usuarios.' : 'No users.')}
                </TableCell>
              </TableRow>
            )}
            {filteredUsers.map(u => {
              const userRoles = u.roles || (u.role ? [u.role] : []);
              const clientName = u.client_id ? (clientsMap[u.client_id] || u.client_id) : '';
              return (
                <TableRow key={u.id || u.email} data-testid={`user-row-${u.email}`} className={u.is_active === false ? 'opacity-60' : ''}>
                  <TableCell className="font-medium text-stone-900">
                    <div className="flex items-center gap-2">
                      <span>{u.name}</span>
                      {u.is_active === false && (
                        <Badge data-testid={`user-inactive-badge-${u.email}`} variant="outline" className="text-[9px] bg-red-50 text-red-700 border-red-300 uppercase tracking-wide">
                          {lang === 'es' ? 'Inactivo' : 'Inactive'}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  {viewMode === 'users' && <TableCell className="text-sm text-stone-700">{u.surname1 || '-'}</TableCell>}
                  {viewMode === 'users' && <TableCell className="text-sm text-stone-600 font-mono">{u.initials || '-'}</TableCell>}
                  <TableCell className="text-sm text-stone-600">{u.email}</TableCell>
                  {viewMode === 'clients' ? (
                    <TableCell className="text-sm text-stone-700">
                      {clientName
                        ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{clientName}</Badge>
                        : <span className="text-xs text-stone-400">-</span>}
                    </TableCell>
                  ) : (
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {userRoles.map(r => (
                          <Badge key={r} variant="outline" className="text-[10px]">{roleLabels[r] || r}</Badge>
                        ))}
                      </div>
                    </TableCell>
                  )}
                  {viewMode === 'users' && <TableCell className="text-sm text-stone-600">{u.is_professional ? <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">{u.specialty}</Badge> : <span className="text-xs text-stone-400">-</span>}</TableCell>}
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-user-${u.email}`} onClick={() => openEdit(u)}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" data-testid={`delete-user-${u.email}`} onClick={() => setDeleteTarget(u)}><Trash size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <Dialog open={showDialog} onOpenChange={v => { if (!v) { setShowDialog(false); setEditingUser(null); } }}>
        <DialogContent className="max-h-[90vh] flex flex-col">
          <DialogHeader><DialogTitle>{editingUser ? (lang === 'es' ? 'Editar Usuario' : 'Edit User') : (lang === 'es' ? 'Nuevo Usuario' : 'New User')}</DialogTitle></DialogHeader>
          <div className="space-y-4 flex-1 overflow-y-auto pr-2 -mr-2">
            {editingUser && (
              <div className={`flex items-center justify-between rounded-sm px-4 py-3 border ${form.is_active ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-300'}`}>
                <div>
                  <Label className="text-sm font-medium text-stone-800">
                    {lang === 'es' ? 'Usuario activo' : 'Active user'}
                  </Label>
                  <p className="text-xs text-stone-600 mt-0.5">
                    {form.is_active
                      ? (lang === 'es' ? 'Puede iniciar sesion y usar el sistema.' : 'Can log in and use the system.')
                      : (lang === 'es' ? 'Desactivado: acceso al sistema bloqueado sin borrar el usuario.' : 'Deactivated: system access blocked without deleting the user.')}
                  </p>
                </div>
                <Switch
                  data-testid="edit-user-is-active"
                  checked={!!form.is_active}
                  disabled={!!(currentUser && editingUser && currentUser.id === editingUser.id)}
                  onCheckedChange={v => setForm(p => ({...p, is_active: v}))}
                />
              </div>
            )}
            <div className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-sm px-4 py-3">
              <Label className="text-sm font-medium text-stone-800">{lang === 'es' ? 'Profesional' : 'Professional'}</Label>
              <Switch data-testid="new-user-professional" checked={form.is_professional} onCheckedChange={v => setForm(p => ({...p, is_professional: v, specialty: v ? p.specialty : ''}))} />
            </div>
            {form.is_professional && (
              <div className="space-y-1.5">
                <Label>{lang === 'es' ? 'Especialidad' : 'Specialty'} <span className="text-red-500">*</span></Label>
                <Select value={form.specialty || '_none'} onValueChange={v => setForm(p => ({...p, specialty: v === '_none' ? '' : v}))}>
                  <SelectTrigger data-testid="new-user-specialty"><SelectValue placeholder={lang === 'es' ? 'Seleccionar especialidad...' : 'Select specialty...'} /></SelectTrigger>
                  <SelectContent>
                    {['Dermatología', 'Ginecología', 'Med. Estética', 'Med. General', 'Odontología', 'Oftalmología', 'Otorrino', 'Podología', 'Traumatología', 'Tricología', 'Urología'].map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5"><Label>{lang === 'es' ? 'Nombre' : 'First Name'} <span className="text-red-500">*</span></Label><Input data-testid="new-user-name" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{lang === 'es' ? 'Primer apellido' : 'First surname'}</Label><Input data-testid="new-user-surname1" value={form.surname1 || ''} onChange={e => setForm(p => ({...p, surname1: e.target.value}))} placeholder={lang === 'es' ? 'Apellido 1' : 'Surname 1'} /></div>
              <div className="space-y-1.5"><Label>{lang === 'es' ? 'Segundo apellido' : 'Second surname'}</Label><Input data-testid="new-user-surname2" value={form.surname2 || ''} onChange={e => setForm(p => ({...p, surname2: e.target.value}))} placeholder={lang === 'es' ? 'Apellido 2' : 'Surname 2'} /></div>
            </div>
            <div className="space-y-1.5"><Label>{lang === 'es' ? 'Iniciales' : 'Initials'} <span className="text-red-500">*</span></Label><Input data-testid="new-user-initials" value={form.initials} onChange={e => setForm(p => ({...p, initials: e.target.value.toUpperCase()}))} placeholder="Ej: FGR" maxLength={5} /></div>
            <div className="space-y-1.5"><Label>Email <span className="text-red-500">*</span></Label><Input data-testid="new-user-email" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} /></div>
            <div className="space-y-1.5"><Label>{lang === 'es' ? 'Telefono movil (para SMS de recordatorios)' : 'Mobile phone (for reminder SMS)'}</Label><Input data-testid="new-user-phone" type="tel" value={form.phone || ''} onChange={e => setForm(p => ({...p, phone: e.target.value}))} placeholder="+34 600 000 000" /></div>
            <div className="space-y-1.5"><Label>{lang === 'es' ? 'DNI / NIE (firma de Investigador Principal)' : 'National ID (Principal Investigator signature)'}</Label><Input data-testid="new-user-dni" value={form.dni || ''} onChange={e => setForm(p => ({...p, dni: e.target.value}))} placeholder="12345678A" /></div>
            {editingUser && !showPasswordField ? (
              <div className="space-y-1.5">
                <Label>{lang === 'es' ? 'Contraseña' : 'Password'}</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="show-password-field-btn"
                  onClick={() => { setShowPasswordField(true); setPasswordTouched(false); setForm(p => ({ ...p, password: '' })); }}
                  className="text-xs gap-2"
                >
                  {lang === 'es' ? 'Cambiar contraseña' : 'Change password'}
                </Button>
                <p className="text-[11px] text-stone-400">
                  {lang === 'es' ? 'La contraseña no se modificará a menos que la cambies explícitamente.' : 'The password will not be changed unless you explicitly set a new one.'}
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>{lang === 'es' ? 'Contraseña' : 'Password'}{!editingUser && ' *'}</Label>
                {/* honeypot anti-autofill: campos señuelo que el navegador rellenará en lugar del real */}
                <input type="text" name="fake-user" autoComplete="username" tabIndex={-1} style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }} aria-hidden="true" />
                <input type="password" name="fake-pass" autoComplete="new-password" tabIndex={-1} style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }} aria-hidden="true" />
                <Input
                  data-testid="new-user-password"
                  type="password"
                  name={`pwd-${editingUser ? editingUser.id : 'new'}`}
                  autoComplete="new-password"
                  autoCorrect="off"
                  spellCheck={false}
                  value={form.password}
                  onFocus={() => setPasswordTouched(true)}
                  onChange={e => { setPasswordTouched(true); setForm(p => ({...p, password: e.target.value})); }}
                  placeholder={editingUser ? (lang === 'es' ? 'Introducir nueva contraseña' : 'Enter new password') : ''}
                />
                {editingUser && (
                  <button
                    type="button"
                    onClick={() => { setShowPasswordField(false); setPasswordTouched(false); setForm(p => ({ ...p, password: '' })); }}
                    className="text-[11px] text-stone-500 hover:text-stone-700 underline"
                  >
                    {lang === 'es' ? 'Cancelar cambio de contraseña' : 'Cancel password change'}
                  </button>
                )}
              </div>
            )}
            {form.is_professional && (
              <div className="border border-stone-200 rounded-sm p-3 bg-stone-50/50 space-y-3" data-testid="pay-per-visit-section">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <Label className="text-sm font-medium text-stone-800">{lang === 'es' ? 'Pago por visita' : 'Pay per visit'}</Label>
                    <span className="text-[11px] text-stone-500">{lang === 'es' ? 'Activa esta opción si al profesional se le liquida un importe fijo por cada visita realizada.' : 'Enable if the professional gets a fixed fee per completed visit.'}</span>
                  </div>
                  <Switch
                    data-testid="pay-per-visit-toggle"
                    checked={!!form.pay_per_visit_enabled}
                    onCheckedChange={v => setForm(p => ({ ...p, pay_per_visit_enabled: v, pay_per_visit_amount: v ? p.pay_per_visit_amount : '' }))}
                  />
                </div>
                {form.pay_per_visit_enabled && (
                  <div className="space-y-1.5">
                    <Label>{lang === 'es' ? 'Precio por visita (€)' : 'Price per visit (€)'} <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="pay-per-visit-amount"
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      value={form.pay_per_visit_amount ?? ''}
                      onChange={e => setForm(p => ({ ...p, pay_per_visit_amount: e.target.value }))}
                      placeholder="0.00"
                    />
                    <p className="text-[11px] text-stone-400">{lang === 'es' ? 'Se utilizará para la liquidación mensual.' : 'Used for the monthly settlement.'}</p>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{lang === 'es' ? 'Roles' : 'Roles'} <span className="text-red-500">*</span></Label>
              <div className="flex flex-wrap gap-2" data-testid="new-user-roles">
                {Object.entries(roleOptions).map(([k, v]) => {
                  const selected = form.roles.includes(k);
                  return (
                    <button
                      key={k}
                      type="button"
                      className={`px-3 py-1.5 rounded-sm border text-xs font-medium transition-colors ${selected ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-stone-200 text-stone-500 hover:border-stone-300'}`}
                      onClick={() => toggleRole(k)}
                      data-testid={`role-toggle-${k}`}
                    >
                      {selected && <Check size={12} className="inline mr-1" />}{v}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{lang === 'es' ? 'Grupos de permisos' : 'Permission groups'}</Label>
              <p className="text-[11px] text-stone-500">
                {lang === 'es'
                  ? 'Si no se selecciona ninguno, se usa el grupo de sistema del rol (comportamiento por defecto). Si seleccionas uno o más, sus permisos se combinan (unión) y SUSTITUYEN a los del rol a nivel APP (menú lateral y permisos transversales). Los permisos dentro de cada estudio siguen viniendo del rol.'
                  : 'If none is selected, the system group bound to the role is used (default). If you select one or more, their permissions are merged (union) and REPLACE the role-based ones at APP level (sidebar menu + transversal permissions). Permissions inside each study still come from the role.'}
              </p>
              {(form.permission_group_ids || []).length > 0 && (
                <div
                  className="flex items-start gap-2 px-3 py-2 rounded-sm bg-amber-50 border border-amber-200 text-[11px] text-amber-800"
                  data-testid="perm-group-override-warning"
                >
                  <span className="font-mono leading-none mt-0.5">⚠</span>
                  <span>
                    {lang === 'es'
                      ? <>Has seleccionado <strong>{(form.permission_group_ids || []).length}</strong> grupo(s) — sus permisos sustituirán a los del rol a nivel APP. El rol seguirá determinando los permisos dentro de los estudios.</>
                      : <>You have selected <strong>{(form.permission_group_ids || []).length}</strong> group(s) — their permissions will replace the role-based ones at APP level. The role still drives study-level permissions.</>}
                  </span>
                </div>
              )}
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto" data-testid="new-user-perm-groups">
                {permGroups.map(g => {
                  const selected = (form.permission_group_ids || []).includes(g.group_id);
                  return (
                    <button
                      key={g.group_id}
                      type="button"
                      className={`px-3 py-1.5 rounded-sm border text-xs font-medium transition-colors ${selected ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-stone-200 text-stone-500 hover:border-stone-300'}`}
                      onClick={() => togglePermGroup(g.group_id)}
                      data-testid={`perm-group-toggle-${g.group_id}`}
                      title={g.description || ''}
                    >
                      {selected && <Check size={12} className="inline mr-1" />}{g.name}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* URS-F416: bloqueos de agenda del usuario (Opcion A + warning C) */}
            {editingUser && (
              <UserScheduleBlocksSection userId={editingUser.id} lang={lang} />
            )}
          </div>
          <DialogFooter>
            {/* URS-F277: estado de bloqueo + boton desbloquear (solo edicion) */}
            {editingUser && lockStatus && (
              <div className="mr-auto flex items-center gap-2 text-xs" data-testid="user-lock-status">
                {lockStatus.locked ? (
                  <>
                    <span className="px-2 py-0.5 rounded-sm bg-red-50 border border-red-200 text-red-700 font-medium" data-testid="user-lock-badge-locked">
                      {lang === 'es' ? 'Bloqueado por intentos fallidos' : 'Locked by failed attempts'}
                    </span>
                    <Button
                      data-testid="user-unlock-btn"
                      onClick={handleUnlock}
                      disabled={unlocking}
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                    >
                      {unlocking ? '...' : (lang === 'es' ? 'Desbloquear' : 'Unlock')}
                    </Button>
                  </>
                ) : (
                  <span className="text-stone-500" data-testid="user-lock-badge-ok">
                    {lockStatus.attempts.length > 0
                      ? (lang === 'es' ? `Intentos recientes: ${lockStatus.attempts.reduce((a,b)=>a+(b.count||0),0)}` : `Recent attempts: ${lockStatus.attempts.reduce((a,b)=>a+(b.count||0),0)}`)
                      : (lang === 'es' ? 'Sin intentos fallidos' : 'No failed attempts')}
                  </span>
                )}
              </div>
            )}
            <Button variant="ghost" onClick={() => { setShowDialog(false); setEditingUser(null); setLockStatus(null); }}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
            <Button data-testid="save-new-user-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : (editingUser ? (lang === 'es' ? 'Guardar' : 'Save') : (lang === 'es' ? 'Crear' : 'Create'))}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ESignature
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onSigned={(signature) => handleDelete(deleteTarget.id, signature)}
        action={lang === 'es' ? `Eliminar usuario ${deleteTarget?.name || ''}` : `Delete user ${deleteTarget?.name || ''}`}
        resource={`user/${deleteTarget?.email || ''}`}
        lang={lang}
      />
    </div>
  );
}

export function VolunteerFieldsTab({ lang }) {
  const [fields, setFields] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [form, setForm] = useState({ tab: 'personal_data', field_key: '', label_es: '', label_en: '', type: 'text', options: [], required: false, order: 0, active: true, full_width: false });
  const [optionsText, setOptionsText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchFields(); }, []);
  const fetchFields = async () => { try { const { data } = await axios.get(`${API_URL}/api/config/volunteer-fields`, { withCredentials: true }); setFields(data); } catch {} };

  const openCreate = () => { setEditingField(null); setForm({ tab: 'personal_data', field_key: '', label_es: '', label_en: '', type: 'text', options: [], required: false, order: fields.length + 1, active: true, full_width: false, obs_required: true, obs_label_es: 'Especificar', obs_label_en: 'Specify' }); setOptionsText(''); setShowDialog(true); };
  const openEdit = (f) => { setEditingField(f); setForm({ obs_required: true, obs_label_es: 'Especificar', obs_label_en: 'Specify', ...f, full_width: !!f.full_width }); setOptionsText((f.options || []).join(', ')); setShowDialog(true); };

  const handleSave = async () => {
    setSaving(true);
    const payload = { ...form, options: optionsText ? optionsText.split(',').map(s => s.trim()).filter(Boolean) : [] };
    try {
      if (editingField) { await axios.put(`${API_URL}/api/config/volunteer-fields/${editingField.id}`, payload, { withCredentials: true }); toast.success(lang === 'es' ? 'Campo actualizado' : 'Field updated'); }
      else { await axios.post(`${API_URL}/api/config/volunteer-fields`, payload, { withCredentials: true }); toast.success(lang === 'es' ? 'Campo creado' : 'Field created'); }
      setShowDialog(false); fetchFields();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const [confirmDeleteField, setConfirmDeleteField] = useState(null);
  const [migrateField, setMigrateField] = useState(null);
  const [migratePreview, setMigratePreview] = useState(null);
  const [migrating, setMigrating] = useState(false);

  // Dry-run al abrir el diálogo de migración.
  useEffect(() => {
    if (!migrateField) { setMigratePreview(null); return; }
    setMigratePreview(null);
    axios.post(`${API_URL}/api/config/volunteer-fields/${migrateField.id}/migrate-boolean-obs?dry_run=true`, {}, { withCredentials: true })
      .then(r => setMigratePreview(r.data))
      .catch(err => { toast.error(errText(err)); setMigrateField(null); });
  }, [migrateField]);

  const runMigration = async () => {
    if (!migrateField) return;
    setMigrating(true);
    try {
      const r = await axios.post(`${API_URL}/api/config/volunteer-fields/${migrateField.id}/migrate-boolean-obs`, {}, { withCredentials: true });
      toast.success(lang === 'es'
        ? `Migración completada: ${r.data.to_si} a "Sí", ${r.data.to_no} a "No" (${r.data.already_object} ya migrados)`
        : `Migration done: ${r.data.to_si} to "Yes", ${r.data.to_no} to "No" (${r.data.already_object} already migrated)`);
      setMigrateField(null);
    } catch (err) { toast.error(errText(err)); }
    finally { setMigrating(false); }
  };

  // --- Importar observaciones desde otra pregunta (merge-obs-from) ---
  const [mergeField, setMergeField] = useState(null);       // destino boolean_obs
  const [mergeSourceKey, setMergeSourceKey] = useState('');  // pregunta origen
  const [mergePreview, setMergePreview] = useState(null);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!mergeField || !mergeSourceKey) { setMergePreview(null); return; }
    setMergePreview(null);
    axios.post(`${API_URL}/api/config/volunteer-fields/${mergeField.id}/merge-obs-from?dry_run=true&source_key=${encodeURIComponent(mergeSourceKey)}`, {}, { withCredentials: true })
      .then(r => setMergePreview(r.data))
      .catch(err => { toast.error(errText(err)); });
  }, [mergeField, mergeSourceKey]);

  const runMergeObs = async () => {
    if (!mergeField || !mergeSourceKey) return;
    setMerging(true);
    try {
      const r = await axios.post(`${API_URL}/api/config/volunteer-fields/${mergeField.id}/merge-obs-from?source_key=${encodeURIComponent(mergeSourceKey)}`, {}, { withCredentials: true });
      toast.success(lang === 'es'
        ? `Importación completada: ${r.data.merged} observaciones movidas (${r.data.skipped_empty} vacías omitidas)`
        : `Import done: ${r.data.merged} notes moved (${r.data.skipped_empty} empty skipped)`);
      setMergeField(null); setMergeSourceKey('');
    } catch (err) { toast.error(errText(err)); }
    finally { setMerging(false); }
  };

  const handleDelete = async (fieldId) => {
    try { await axios.delete(`${API_URL}/api/config/volunteer-fields/${fieldId}`, { withCredentials: true }); toast.success(lang === 'es' ? 'Campo eliminado' : 'Field deleted'); setConfirmDeleteField(null); fetchFields(); }
    catch (err) { toast.error(errText(err)); }
  };

  const FieldSection = ({ items, title }) => (
    <div className="mb-6">
      <h3 className="text-xs font-semibold text-stone-500 mb-3 uppercase tracking-[0.05em]">{title}</h3>
      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <Table className="dense-table">
          <TableHeader><TableRow>
            <TableHead className="w-12">#</TableHead><TableHead>Key</TableHead>
            <TableHead>{lang === 'es' ? 'Etiqueta ES' : 'Label ES'}</TableHead>
            <TableHead>{lang === 'es' ? 'Etiqueta EN' : 'Label EN'}</TableHead>
            <TableHead>{lang === 'es' ? 'Tipo' : 'Type'}</TableHead>
            <TableHead className="w-16">Req.</TableHead><TableHead className="w-20"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.sort((a, b) => a.order - b.order).map(f => (
              <TableRow key={f.id} data-testid={`field-row-${f.field_key}`}>
                <TableCell className="font-mono text-xs">{f.order}</TableCell>
                <TableCell className="font-mono text-xs text-stone-600">{f.field_key}</TableCell>
                <TableCell className="text-sm">{f.label_es}</TableCell>
                <TableCell className="text-sm">{f.label_en}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{lang === 'es' ? ({text:'Texto',number:'Numero',date:'Fecha',select:'Selector',multiselect:'Multiseleccion',textarea:'Area de texto',checkbox:'Casilla','boolean_obs':'Sí/No + obs.'})[f.type] || f.type : f.type}</Badge></TableCell>
                <TableCell>{f.required ? <Check size={14} className="text-green-600" /> : '-'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-field-${f.field_key}`} onClick={() => openEdit(f)}><Pencil size={14} /></Button>
                    {f.type === 'boolean_obs' && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-[#1f3e80] hover:text-[#2a4f8a]" data-testid={`migrate-field-${f.field_key}`} onClick={() => setMigrateField(f)} title={lang === 'es' ? 'Migrar datos antiguos a Sí/No + observación' : 'Migrate legacy data to Yes/No + note'}><ArrowsClockwise size={14} /></Button>
                    )}
                    {f.type === 'boolean_obs' && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600 hover:text-emerald-700" data-testid={`merge-obs-field-${f.field_key}`} onClick={() => { setMergeField(f); setMergeSourceKey(''); }} title={lang === 'es' ? 'Importar observaciones desde otra pregunta' : 'Import notes from another question'}><ArrowLineDown size={14} /></Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" data-testid={`delete-field-${f.field_key}`} onClick={() => setConfirmDeleteField(f)}><Trash size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="create-field-btn" onClick={openCreate} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2"><Plus size={16} />{lang === 'es' ? 'Nuevo Campo' : 'New Field'}</Button>
      </div>
      <FieldSection items={fields.filter(f => f.tab === 'personal_data')} title={lang === 'es' ? 'Datos Personales' : 'Personal Data'} />
      <FieldSection items={fields.filter(f => f.tab === 'health_questionnaire')} title={lang === 'es' ? 'Cuestionario de Salud' : 'Health Questionnaire'} />
      <DiseaseGroupsManager lang={lang} />
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingField ? (lang === 'es' ? 'Editar Campo' : 'Edit Field') : (lang === 'es' ? 'Nuevo Campo' : 'New Field')}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>{lang === 'es' ? 'Pestana' : 'Tab'}</Label>
                <Select value={form.tab} onValueChange={v => setForm(p => ({...p, tab: v}))}><SelectTrigger data-testid="field-tab-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="personal_data">{lang === 'es' ? 'Datos Personales' : 'Personal Data'}</SelectItem><SelectItem value="health_questionnaire">{lang === 'es' ? 'Q Salud' : 'Health Q'}</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Key</Label><Input data-testid="field-key-input" value={form.field_key} onChange={e => setForm(p => ({...p, field_key: e.target.value.toLowerCase().replace(/\s/g, '_')}))} placeholder="field_name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Label ES</Label><Input data-testid="field-label-es" value={form.label_es} onChange={e => setForm(p => ({...p, label_es: e.target.value}))} /></div>
              <div className="space-y-1.5"><Label>Label EN</Label><Input data-testid="field-label-en" value={form.label_en} onChange={e => setForm(p => ({...p, label_en: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>{lang === 'es' ? 'Tipo' : 'Type'}</Label>
                <Select value={form.type} onValueChange={v => setForm(p => ({...p, type: v}))}><SelectTrigger data-testid="field-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{['text', 'number', 'date', 'select', 'multiselect', 'textarea', 'checkbox', 'boolean_obs'].map(t => (<SelectItem key={t} value={t}>{lang === 'es' ? ({text:'Texto',number:'Numero',date:'Fecha',select:'Selector',multiselect:'Multiseleccion',textarea:'Area de texto',checkbox:'Casilla','boolean_obs':'Sí/No + observación condicional'})[t] : ({boolean_obs:'Yes/No + conditional note'})[t] || t}</SelectItem>))}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>{lang === 'es' ? 'Orden' : 'Order'}</Label><Input data-testid="field-order-input" type="number" value={form.order} onChange={e => setForm(p => ({...p, order: parseInt(e.target.value) || 0}))} /></div>
            </div>
            {['select', 'multiselect'].includes(form.type) && <div className="space-y-1.5"><Label>{lang === 'es' ? 'Opciones (separadas por coma)' : 'Options (comma separated)'}</Label><Input data-testid="field-options-input" value={optionsText} onChange={e => setOptionsText(e.target.value)} placeholder="Option 1, Option 2" /></div>}
            {form.type === 'boolean_obs' && (
              <div className="space-y-3 p-3 bg-sky-50 border border-sky-200 rounded-sm" data-testid="boolean-obs-config">
                <p className="text-[11px] text-sky-800">{lang === 'es' ? 'Al elegir "Sí" aparece un campo de observación. Configura su etiqueta y si es obligatorio.' : 'When "Yes" is chosen, a note field appears. Configure its label and whether it is required.'}</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>{lang === 'es' ? 'Etiqueta observación (ES)' : 'Note label (ES)'}</Label><Input data-testid="field-obs-label-es" value={form.obs_label_es || ''} onChange={e => setForm(p => ({ ...p, obs_label_es: e.target.value }))} className="bg-white" /></div>
                  <div className="space-y-1.5"><Label>{lang === 'es' ? 'Etiqueta observación (EN)' : 'Note label (EN)'}</Label><Input data-testid="field-obs-label-en" value={form.obs_label_en || ''} onChange={e => setForm(p => ({ ...p, obs_label_en: e.target.value }))} className="bg-white" /></div>
                </div>
                <div className="flex items-center gap-2"><Switch data-testid="field-obs-required-switch" checked={form.obs_required !== false} onCheckedChange={v => setForm(p => ({ ...p, obs_required: v }))} /><Label>{lang === 'es' ? 'Observación obligatoria si "Sí"' : 'Note required when "Yes"'}</Label></div>
                <div className="flex items-center gap-2"><Switch data-testid="field-group-selector-switch" checked={!!form.group_selector} onCheckedChange={v => setForm(p => ({ ...p, group_selector: v }))} /><Label>{lang === 'es' ? 'Mostrar selector de Grupo de enfermedad (varias filas Grupo + Observación)' : 'Show disease group selector (multiple Group + Note rows)'}</Label></div>
              </div>
            )}
            <div className="flex gap-6">
              <div className="flex items-center gap-2"><Switch data-testid="field-required-switch" checked={form.required} onCheckedChange={v => setForm(p => ({...p, required: v}))} /><Label>{lang === 'es' ? 'Obligatorio' : 'Required'}</Label></div>
              <div className="flex items-center gap-2"><Switch data-testid="field-active-switch" checked={form.active} onCheckedChange={v => setForm(p => ({...p, active: v}))} /><Label>{lang === 'es' ? 'Activo' : 'Active'}</Label></div>
              <div className="flex items-center gap-2"><Switch data-testid="field-fullwidth-switch" checked={!!form.full_width} onCheckedChange={v => setForm(p => ({...p, full_width: v}))} /><Label>{lang === 'es' ? 'Línea completa' : 'Full row'}</Label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
            <Button data-testid="save-field-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : (lang === 'es' ? 'Guardar' : 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDeleteDialog
        open={!!confirmDeleteField}
        onOpenChange={(v) => { if (!v) setConfirmDeleteField(null); }}
        title={lang === 'es' ? 'Eliminar campo' : 'Delete field'}
        description={lang === 'es' ? `¿Estás seguro de que deseas eliminar el campo "${confirmDeleteField?.field_key || ''}"?` : `Are you sure you want to delete field "${confirmDeleteField?.field_key || ''}"?`}
        onConfirm={() => handleDelete(confirmDeleteField?.id)}
      />

      <Dialog open={!!migrateField} onOpenChange={(v) => { if (!v && !migrating) setMigrateField(null); }}>
        <DialogContent className="max-w-md" data-testid="migrate-boolean-obs-dialog">
          <DialogHeader>
            <DialogTitle>{lang === 'es' ? 'Migrar datos antiguos a Sí/No + observación' : 'Migrate legacy data'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-stone-600">
              {lang === 'es'
                ? <>Campo <b>{migrateField?.label_es}</b> (<span className="font-mono text-xs">{migrateField?.field_key}</span>).</>
                : <>Field <b>{migrateField?.label_en}</b> (<span className="font-mono text-xs">{migrateField?.field_key}</span>).</>}
            </p>
            <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-xs text-stone-600 space-y-1">
              <p>{lang === 'es' ? 'Reglas de conversión:' : 'Conversion rules:'}</p>
              <p>• {lang === 'es' ? 'Vacío / "no" / "ninguna" / "nada" → No' : 'Empty / "no" / "none" → No'}</p>
              <p>• {lang === 'es' ? 'Cualquier otro texto → Sí + observación' : 'Any other text → Yes + note'}</p>
            </div>
            {!migratePreview ? (
              <p className="text-stone-400 italic">{lang === 'es' ? 'Calculando previsualización...' : 'Computing preview...'}</p>
            ) : (
              <div className="bg-sky-50 border border-sky-200 rounded-sm p-3 text-xs space-y-1" data-testid="migrate-preview">
                <p><b>{migratePreview.to_si}</b> {lang === 'es' ? 'pasarán a "Sí" (con observación)' : 'to "Yes"'}</p>
                <p><b>{migratePreview.to_no}</b> {lang === 'es' ? 'pasarán a "No"' : 'to "No"'}</p>
                <p className="text-stone-500">{migratePreview.already_object} {lang === 'es' ? 'ya migrados (se omiten)' : 'already migrated (skipped)'}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMigrateField(null)} disabled={migrating} data-testid="migrate-cancel-btn">{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
            <Button className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white" onClick={runMigration} disabled={migrating || !migratePreview} data-testid="migrate-confirm-btn">
              {migrating ? (lang === 'es' ? 'Migrando...' : 'Migrating...') : (lang === 'es' ? 'Migrar ahora' : 'Migrate now')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!mergeField} onOpenChange={(v) => { if (!v && !merging) { setMergeField(null); setMergeSourceKey(''); } }}>
        <DialogContent className="max-w-md" data-testid="merge-obs-dialog">
          <DialogHeader>
            <DialogTitle>{lang === 'es' ? 'Importar observaciones desde otra pregunta' : 'Import notes from another question'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-stone-600">
              {lang === 'es'
                ? <>Destino: <b>{mergeField?.label_es}</b> (<span className="font-mono text-xs">{mergeField?.field_key}</span>).</>
                : <>Target: <b>{mergeField?.label_en}</b> (<span className="font-mono text-xs">{mergeField?.field_key}</span>).</>}
            </p>
            <div className="space-y-1.5">
              <Label>{lang === 'es' ? 'Pregunta origen (texto a mover)' : 'Source question (text to move)'}</Label>
              <Select value={mergeSourceKey} onValueChange={setMergeSourceKey}>
                <SelectTrigger data-testid="merge-source-select"><SelectValue placeholder={lang === 'es' ? 'Selecciona pregunta…' : 'Select question…'} /></SelectTrigger>
                <SelectContent>
                  {fields
                    .filter(f => f.id !== mergeField?.id && (f.tab || 'health_questionnaire') === (mergeField?.tab || 'health_questionnaire'))
                    .map(f => (<SelectItem key={f.id} value={f.field_key}>{(lang === 'es' ? f.label_es : f.label_en) || f.field_key} ({f.field_key})</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-xs text-stone-600 space-y-1">
              <p>{lang === 'es' ? 'Reglas:' : 'Rules:'}</p>
              <p>• {lang === 'es' ? 'El texto de la pregunta origen se copia a la observación del destino.' : 'Source text is copied into the target note.'}</p>
              <p>• {lang === 'es' ? 'No se modifica el Sí/No del destino ya migrado.' : 'Target Yes/No value is not modified.'}</p>
              <p>• {lang === 'es' ? 'Si el destino ya tiene observación, se concatenan.' : 'If the target already has a note, both are concatenated.'}</p>
              <p>• {lang === 'es' ? 'La pregunta origen se vacía tras migrar.' : 'The source question is cleared after migration.'}</p>
            </div>
            {!mergeSourceKey ? null : !mergePreview ? (
              <p className="text-stone-400 italic">{lang === 'es' ? 'Calculando previsualización...' : 'Computing preview...'}</p>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs space-y-1" data-testid="merge-preview">
                <p><b>{mergePreview.merged}</b> {lang === 'es' ? 'observaciones se moverán al destino' : 'notes will be moved'}</p>
                <p className="text-stone-500">{mergePreview.skipped_empty} {lang === 'es' ? 'voluntarios con origen vacío (se omiten)' : 'empty sources (skipped)'}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMergeField(null); setMergeSourceKey(''); }} disabled={merging} data-testid="merge-cancel-btn">{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={runMergeObs} disabled={merging || !mergeSourceKey || !mergePreview} data-testid="merge-confirm-btn">
              {merging ? (lang === 'es' ? 'Importando...' : 'Importing...') : (lang === 'es' ? 'Importar ahora' : 'Import now')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function QuestionBankTab({ lang }) {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dermScales, setDermScales] = useState([]);
  const [filterCat, setFilterCat] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ text_es: '', text_en: '', type: 'text', category: 'general', options: [], active: true, scale_id: null });
  const [optionsText, setOptionsText] = useState('');
  const [cellOptionsText, setCellOptionsText] = useState('');
  const [saving, setSaving] = useState(false);

  const lb = lang === 'es'
    ? { title: 'Banco de Preguntas', new: 'Nueva Pregunta', edit: 'Editar Pregunta', text_es: 'Texto (ES)', text_en: 'Texto (EN)', type: 'Tipo', category: 'Categoria', options: 'Opciones (separadas por coma)', active: 'Activa', save: 'Guardar', cancel: 'Cancelar', all: 'Todas', confirm_delete: 'Eliminar esta pregunta?', created: 'Pregunta creada', updated: 'Pregunta actualizada', deleted: 'Pregunta eliminada', no_questions: 'No hay preguntas en el banco' }
    : { title: 'Question Bank', new: 'New Question', edit: 'Edit Question', text_es: 'Text (ES)', text_en: 'Text (EN)', type: 'Type', category: 'Category', options: 'Options (comma separated)', active: 'Active', save: 'Save', cancel: 'Cancel', all: 'All', confirm_delete: 'Delete this question?', created: 'Question created', updated: 'Question updated', deleted: 'Question deleted', no_questions: 'No questions in bank' };

  const catLabels = { tolerancia: 'Tolerancia', eficacia: 'Eficacia', seguridad: 'Seguridad', dermatologia: 'Dermatologia', general: 'General' };

  const load = () => {
    Promise.all([
      axios.get(`${API_URL}/api/config/questions`, { withCredentials: true }),
      axios.get(`${API_URL}/api/config/question-categories`, { withCredentials: true }),
      axios.get(`${API_URL}/api/config/derm-scales`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]).then(([qr, cr, sr]) => {
      setQuestions(qr.data);
      const allCats = [...new Set([...defaultCategories, ...cr.data])];
      setCategories(allCats);
      setDermScales(sr.data || []);
    }).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const filtered = filterCat === 'all' ? questions : questions.filter(q => q.category === filterCat);

  const openCreate = () => {
    setEditing(null);
    setForm({ text_es: '', text_en: '', type: 'text', category: 'general', options: [], active: true, scale_id: null, cell_type: 'text', cell_options: [], show_observations: true, table_rows: 2, table_cols: 2, table_corner: '', table_headers_row: ['', ''], table_headers_col: [''], table_cell_types: [['text']], composite_items: [], composite_total_label: 'Total' });
    setOptionsText('');
    setCellOptionsText('');
    setShowDialog(true);
  };

  const openEdit = (q) => {
    setEditing(q);
    // URS-F265: si la pregunta es Likert con scale_size=7 mostramos
    // 'likert_1_7' en el dropdown; el resto se trata como 'likert_1_5'.
    let dropdownType = q.type;
    if (q.type === 'likert' || q.type === 'likert_1_5') {
      dropdownType = q.scale_size === 7 ? 'likert_1_7' : 'likert_1_5';
    }
    setForm({
      text_es: q.text_es, text_en: q.text_en || '', type: dropdownType, category: q.category,
      options: q.options || [], active: q.active !== false, scale_id: q.scale_id || null,
      cell_type: q.cell_type || 'text', cell_options: q.cell_options || [],
      show_observations: q.show_observations !== false,
      // URS-F352: campos de tabla personalizada
      table_rows: q.table_rows || 2,
      table_cols: q.table_cols || 2,
      table_corner: q.table_corner || '',
      table_headers_row: q.table_headers_row || [],
      table_headers_col: q.table_headers_col || [],
      table_cell_types: q.table_cell_types || [],
      // URS-F358: composite_scale
      composite_items: q.composite_items || [],
      composite_total_label: q.composite_total_label || 'Total',
    });
    setOptionsText((q.options || []).join(', '));
    setCellOptionsText((q.cell_options || []).join(', '));
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.text_es.trim()) { toast.error(lang === 'es' ? 'Falta el enunciado de la pregunta (texto en español)' : 'The question wording is required (Spanish text)'); return; }
    if (form.type === 'derm_scale' && !form.scale_id) {
      toast.error(lang === 'es' ? 'Selecciona una escala dermatológica' : 'Select a dermatological scale');
      return;
    }
    const optionsArr = optionsText ? optionsText.split(',').map(s => s.trim()).filter(Boolean) : [];
    const cellOptionsArr = cellOptionsText ? cellOptionsText.split(',').map(s => s.trim()).filter(Boolean) : [];
    if ((form.type === 'table_text' || form.type === 'table_select') && optionsArr.length === 0) {
      toast.error(lang === 'es' ? 'La tabla necesita al menos una fila (opcion)' : 'Table needs at least one row (option)');
      return;
    }
    if (form.type === 'table_select' && cellOptionsArr.length === 0) {
      toast.error(lang === 'es' ? 'Define al menos una opcion de respuesta para el selector' : 'Define at least one answer option for the selector');
      return;
    }
    if (form.type === 'nps_multi' && optionsArr.length === 0) {
      toast.error(lang === 'es' ? 'NPS múltiple necesita al menos un sub-apartado' : 'Multi NPS needs at least one sub-item');
      return;
    }
    if (form.type === 'checklist' && optionsArr.length === 0) {
      toast.error(lang === 'es' ? 'La lista de verificación necesita al menos un item' : 'Checklist needs at least one item');
      return;
    }
    if (form.type === 'composite_scale') {
      const valid = (form.composite_items || []).filter(it => it && it.label && it.scale_id);
      if (valid.length === 0) {
        toast.error(lang === 'es' ? 'La escala compuesta necesita al menos un sub-apartado con escala asignada' : 'Composite scale needs at least one sub-item with assigned scale');
        return;
      }
    }
    setSaving(true);
    // URS-F265: tipos Likert llevan escala fija. Convertimos el value 'likert_1_7'
    // (placeholder del dropdown) al modelo unificado type='likert' + scale_size=7,
    // y rellenamos options automaticamente. likert_1_5 mantiene retrocompat.
    let finalType = form.type;
    let finalOptions = optionsArr;
    let finalScaleSize = null;
    if (form.type === 'likert_1_5' || form.type === 'likert_1_7' || form.type === 'likert') {
      finalScaleSize = form.type === 'likert_1_7' ? 7 : 5;
      finalType = finalScaleSize === 7 ? 'likert' : 'likert_1_5';
      finalOptions = Array.from({ length: finalScaleSize }, (_, i) => String(i + 1));
    }
    const payload = {
      ...form,
      type: finalType,
      options: finalOptions,
      scale_id: form.type === 'derm_scale' ? form.scale_id : null,
      cell_type: form.type === 'table_text' ? (form.cell_type || 'text') : 'text',
      cell_options: form.type === 'table_select' ? cellOptionsArr : [],
      show_observations: (form.type === 'table_text' || form.type === 'table_select' || form.type === 'custom_table') ? (form.show_observations !== false) : false,
      scale_size: finalScaleSize,
      // URS-F352: solo persistimos campos de tabla personalizada cuando el tipo lo requiere
      table_rows: form.type === 'custom_table' ? (form.table_rows || 2) : 0,
      table_cols: form.type === 'custom_table' ? (form.table_cols || 2) : 0,
      table_corner: form.type === 'custom_table' ? (form.table_corner || '') : '',
      table_headers_row: form.type === 'custom_table' ? (form.table_headers_row || []) : [],
      table_headers_col: form.type === 'custom_table' ? (form.table_headers_col || []) : [],
      table_cell_types: form.type === 'custom_table' ? (form.table_cell_types || []) : [],
      // URS-F358: composite_scale fields
      composite_items: form.type === 'composite_scale' ? (form.composite_items || []).filter(it => it && it.label && it.scale_id) : [],
      composite_total_label: form.type === 'composite_scale' ? (form.composite_total_label || 'Total') : 'Total',
    };
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/config/questions/${editing.question_id}`, payload, { withCredentials: true });
        toast.success(lb.updated);
      } else {
        await axios.post(`${API_URL}/api/config/questions`, payload, { withCredentials: true });
        toast.success(lb.created);
      }
      setShowDialog(false);
      load();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const [confirmDeleteQ, setConfirmDeleteQ] = useState(null);
  // URS-F353: vista previa de una única pregunta del banco.
  const [previewQ, setPreviewQ] = useState(null);

  const handleDelete = async (qId) => {
    try {
      await axios.delete(`${API_URL}/api/config/questions/${qId}`, { withCredentials: true });
      toast.success(lb.deleted);
      setConfirmDeleteQ(null);
      load();
    } catch (err) { toast.error(errText(err)); }
  };

  const handleDuplicate = async (qId) => {
    try {
      await axios.post(`${API_URL}/api/config/questions/${qId}/duplicate`, {}, { withCredentials: true });
      toast.success(lang === 'es' ? 'Pregunta duplicada' : 'Question duplicated');
      load();
    } catch (err) { toast.error(errText(err)); }
  };

  const catColors = { tolerancia: 'bg-amber-50 text-amber-700 border-amber-200', eficacia: 'bg-green-50 text-green-700 border-green-200', seguridad: 'bg-red-50 text-red-700 border-red-200', dermatologia: 'bg-cyan-50 text-cyan-700 border-cyan-200', general: 'bg-stone-50 text-stone-600 border-stone-200' };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger data-testid="question-filter-category" className="w-40 bg-white border-stone-200 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lb.all}</SelectItem>
              {categories.map(c => (<SelectItem key={c} value={c}>{catLabels[c] || c}</SelectItem>))}
            </SelectContent>
          </Select>
          <Badge variant="outline" className="text-xs font-mono">{filtered.length}</Badge>
        </div>
        <Button data-testid="create-question-btn" onClick={openCreate} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
          <Plus size={16} />{lb.new}
        </Button>
      </div>

      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center"><p className="text-sm text-stone-500">{lb.no_questions}</p></div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>{lb.text_es}</TableHead>
              <TableHead className="w-28">{lb.type}</TableHead>
              <TableHead className="w-32">{lb.category}</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map(q => (
                <TableRow key={q.question_id} data-testid={`question-row-${q.question_id}`}>
                  <TableCell className="text-sm text-stone-900">{q.text_es}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{questionTypeLabels[lang]?.[q.type] || q.type}</Badge></TableCell>
                  <TableCell><Badge variant="outline" className={`text-xs ${catColors[q.category] || catColors.general}`}>{catLabels[q.category] || q.category}</Badge></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-500 hover:text-[#1f3e80]" title={lang === 'es' ? 'Vista previa' : 'Preview'} data-testid={`preview-question-${q.question_id}`} onClick={() => setPreviewQ(q)}><Eye size={14} /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-question-${q.question_id}`} onClick={() => openEdit(q)}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-500 hover:text-[#1f3e80]" title={lang === 'es' ? 'Duplicar' : 'Duplicate'} data-testid={`duplicate-question-${q.question_id}`} onClick={() => handleDuplicate(q.question_id)}><Copy size={14} /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" data-testid={`delete-question-${q.question_id}`} onClick={() => setConfirmDeleteQ(q)}><Trash size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? lb.edit : lb.new}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-1.5">
              <Label>{lb.text_es} <span className="text-red-500">*</span></Label>
              <Input data-testid="question-text-es" value={form.text_es} onChange={e => setForm(p => ({ ...p, text_es: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>{lb.text_en}</Label>
              <Input data-testid="question-text-en" value={form.text_en} onChange={e => setForm(p => ({ ...p, text_en: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{lb.type}</Label>
                <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger data-testid="question-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(questionTypeLabels[lang] || questionTypeLabels.es).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{lb.category}</Label>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="question-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (<SelectItem key={c} value={c}>{catLabels[c] || c}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(form.type === 'likert_1_5' || form.type === 'likert_1_7' || form.type === 'likert') && (
              <div className="bg-violet-50 border border-violet-200 rounded-sm p-3 text-xs text-violet-800">
                {form.type === 'likert_1_7'
                  ? (lang === 'es' ? 'Escala fija de 1 a 7. Etiquetas: 1 = Me disgusta mucho · 7 = Me gusta mucho. No es necesario rellenar opciones.' : 'Fixed 1 to 7 scale. Labels: 1 = Strongly dislike · 7 = Strongly like. No need to fill options.')
                  : (lang === 'es' ? 'Escala fija de 1 a 5. Etiquetas: 1 = Totalmente en desacuerdo · 5 = Totalmente de acuerdo. No es necesario rellenar opciones.' : 'Fixed 1 to 5 scale. Labels: 1 = Strongly disagree · 5 = Strongly agree. No need to fill options.')}
              </div>
            )}
            {['scale', 'select', 'multiselect', 'table_text', 'table_select', 'nps_multi', 'checklist'].includes(form.type) && (
              <div className="space-y-1.5">
                <Label>{(form.type === 'table_text' || form.type === 'table_select') ? (lang === 'es' ? 'Filas de la tabla' : 'Table rows') : (form.type === 'nps_multi' ? (lang === 'es' ? 'Sub-apartados (cada uno con su escala 0-10)' : 'Sub-items (each with 0-10 scale)') : (form.type === 'checklist' ? (lang === 'es' ? 'Items de verificación (uno por checkbox)' : 'Checklist items (one per checkbox)') : lb.options))}</Label>
                <Input data-testid="question-options-input" value={optionsText} onChange={e => setOptionsText(e.target.value)} placeholder={(form.type === 'table_text' || form.type === 'table_select') ? (lang === 'es' ? 'Producto 1, Producto 2, ##Seccion 2, Producto 3' : 'Item 1, Item 2, ##Section 2, Item 3') : (form.type === 'nps_multi' ? (lang === 'es' ? 'Recomendarías a un familiar, Volverías a comprar, Calidad/precio' : 'Would recommend, Would buy again, Value for money') : (form.type === 'checklist' ? (lang === 'es' ? 'Alergias conocidas, Embarazo, Lactancia, Medicación actual' : 'Known allergies, Pregnancy, Lactation, Current medication') : 'Opcion 1, Opcion 2, Opcion 3'))} />
                {(form.type === 'table_text' || form.type === 'table_select') && (
                  <>
                    <p className="text-[11px] text-stone-500">{lang === 'es' ? 'Cada opcion sera una fila de la tabla. Al lado de cada fila se mostrara un campo para escribir.' : 'Each option becomes a table row with a free-text field beside it.'}</p>
                    <p className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded-sm px-2 py-1.5">
                      <strong>{lang === 'es' ? 'Secciones (opcional):' : 'Sections (optional):'}</strong>{' '}
                      {lang === 'es'
                        ? 'antepon "##" al nombre para crear un encabezado de sección. Ejemplo: ##Cara, Picor, Eritema, ##Cuello, Sequedad → renderiza dos secciones agrupando las filas siguientes.'
                        : 'prefix a name with "##" to insert a section header. Example: ##Face, Itch, Redness, ##Neck, Dryness.'}
                    </p>
                  </>
                )}
              </div>
            )}
            {form.type === 'table_text' && (
              <div className="space-y-1.5 bg-sky-50 border border-sky-200 rounded-sm p-3">
                <Label className="text-sky-800">{lang === 'es' ? 'Tipo de celda' : 'Cell type'}</Label>
                <Select value={form.cell_type || 'text'} onValueChange={v => setForm(p => ({ ...p, cell_type: v }))}>
                  <SelectTrigger data-testid="question-cell-type-select" className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">{lang === 'es' ? 'Texto libre' : 'Free text'}</SelectItem>
                    <SelectItem value="number">{lang === 'es' ? 'Numerico' : 'Numeric'}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-sky-700">{lang === 'es' ? 'Al rellenar el cuestionario se muestra una tabla: a la izquierda el nombre de cada opcion y a la derecha (columna "Respuesta") un campo del tipo elegido. Todas las filas son obligatorias para finalizar.' : 'A two-column table is shown: option name on the left and an answer field of the chosen type on the right (column "Respuesta"). All rows must be filled to finalize.'}</p>
              </div>
            )}
            {form.type === 'table_select' && (
              <div className="space-y-1.5 bg-sky-50 border border-sky-200 rounded-sm p-3">
                <Label className="text-sky-800">{lang === 'es' ? 'Opciones de respuesta (selector)' : 'Answer options (selector)'} <span className="text-red-500">*</span></Label>
                <Input data-testid="question-cell-options-input" value={cellOptionsText} onChange={e => setCellOptionsText(e.target.value)} placeholder={lang === 'es' ? 'Bueno, Regular, Malo' : 'Good, Average, Bad'} className="bg-white" />
                <p className="text-[11px] text-sky-700">{lang === 'es' ? 'Cada fila de la tabla tendra un selector con estas opciones precargadas. Todas las filas son obligatorias para finalizar.' : 'Each row will show a dropdown with these predefined options. All rows must be filled to finalize.'}</p>
              </div>
            )}
            {(form.type === 'table_text' || form.type === 'table_select' || form.type === 'custom_table') && (
              <div className="flex items-center gap-2">
                <Switch
                  data-testid="question-show-observations-switch"
                  checked={form.show_observations !== false}
                  onCheckedChange={v => setForm(p => ({ ...p, show_observations: v }))}
                />
                <Label className="text-sm">{lang === 'es' ? 'Mostrar campo de Observaciones debajo de la tabla' : 'Show Observations field below the table'}</Label>
              </div>
            )}
            {form.type === 'custom_table' && (
              <div className="space-y-1.5 bg-violet-50/40 border border-violet-200 rounded-sm p-3">
                <Label className="text-violet-800">{lang === 'es' ? 'Diseño de la tabla personalizada' : 'Custom table design'}</Label>
                <CustomTableDesigner
                  value={form}
                  onChange={patch => setForm(p => ({ ...p, ...patch }))}
                  lang={lang}
                />
                <p className="text-[11px] text-violet-700">{lang === 'es' ? 'Define cabeceras (1ª fila y 1ª columna) y el tipo de cada celda interior. Min 2x2, max 10x10. Todas las celdas serán obligatorias al cumplimentar.' : 'Define headers (first row & first column) and the type of each inner cell. Min 2x2, max 10x10. All cells required to finalize.'}</p>
              </div>
            )}
            {form.type === 'composite_scale' && (
              <div className="space-y-1.5 bg-violet-50/40 border border-violet-200 rounded-sm p-3">
                <Label className="text-violet-800">{lang === 'es' ? 'Diseño de la escala compuesta' : 'Composite scale design'}</Label>
                <CompositeScaleDesigner
                  value={form}
                  onChange={patch => setForm(p => ({ ...p, ...patch }))}
                  lang={lang}
                />
                <p className="text-[11px] text-violet-700">{lang === 'es' ? 'Define los sub-apartados (ej. OECS: Eritema, Edema, Secreción, Dolor). Cada uno referencia una escala dermatológica existente — la imagen y los niveles se heredan de esa escala. La puntuación total se calcula como suma simple.' : 'Define sub-items (e.g. OECS: Erythema, Edema, Secretion, Pain). Each references an existing dermatological scale — image and levels are inherited. Total is a simple sum.'}</p>
              </div>
            )}
            {form.type === 'derm_scale' && (
              <div className="space-y-1.5 bg-violet-50 border border-violet-200 rounded-sm p-3">
                <Label className="text-violet-800">{lang === 'es' ? 'Escala dermatológica' : 'Dermatological scale'} <span className="text-red-500">*</span></Label>
                <Select value={form.scale_id || ''} onValueChange={v => setForm(p => ({ ...p, scale_id: v }))}>
                  <SelectTrigger data-testid="question-derm-scale-select"><SelectValue placeholder={lang === 'es' ? 'Selecciona una escala…' : 'Select a scale…'} /></SelectTrigger>
                  <SelectContent>
                    {dermScales.length === 0
                      ? <div className="text-xs text-stone-400 p-2">{lang === 'es' ? 'Sin escalas. Créalas en la pestaña Escalas dermatológicas.' : 'No scales. Create them in the Dermatological Scales tab.'}</div>
                      : dermScales.map(s => (
                          <SelectItem key={s.scale_id} value={s.scale_id}>
                            {s.name} <span className="text-stone-400 text-xs">({(s.options || []).length} niveles)</span>
                          </SelectItem>
                        ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-violet-700">
                  {lang === 'es'
                    ? 'Al cumplimentar el cuestionario, esta pregunta mostrará los niveles de la escala como botones y un botón para ver la imagen de referencia en popup.'
                    : 'When filling the questionnaire, scale levels are shown as buttons + a reference image popup.'}
                </p>
              </div>
            )}
            {form.type === 'masi' && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                <Label className="text-amber-800 font-semibold">{lang === 'es' ? 'Escala MASI compuesta' : 'Composite MASI score'}</Label>
                <p className="text-[11px] text-amber-700 mt-1">
                  {lang === 'es'
                    ? 'Esta pregunta utiliza la escala MASI estándar internacional (Melasma Area and Severity Index). Al cumplimentar, se muestra automaticamente las 4 regiones faciales (Frente 30%, Mentón 10%, Malar Dcha 30%, Malar Izq 30%) con sus 3 componentes (Área 0-6, Oscuridad 0-4, Homogeneidad 0-4) y se calcula la puntuación total en vivo según la fórmula MASI = 0,3(AF×DF×HF) + 0,1(AC×DC×HC) + 0,3(ARM×DRM×HRM) + 0,3(ALM×DLM×HLM). Rango total: 0-96.'
                    : 'This question uses the standard MASI score (international). At fill time, 4 facial regions with 3 components each are shown and total is computed live (0-96).'}
                </p>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch data-testid="question-active-switch" checked={form.active} onCheckedChange={v => setForm(p => ({ ...p, active: v }))} />
              <Label>{lb.active}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>{lb.cancel}</Button>
            <Button data-testid="save-question-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : lb.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDeleteDialog
        open={!!confirmDeleteQ}
        onOpenChange={(v) => { if (!v) setConfirmDeleteQ(null); }}
        title={lang === 'es' ? 'Eliminar pregunta' : 'Delete question'}
        description={lang === 'es' ? `¿Estás seguro de que deseas eliminar esta pregunta?` : `Are you sure you want to delete this question?`}
        onConfirm={() => handleDelete(confirmDeleteQ?.question_id)}
      />
      <QuestionnairePreviewDialog
        open={!!previewQ}
        onClose={() => setPreviewQ(null)}
        questionnaire={previewQ ? { name: previewQ.text_es || previewQ.text_en || 'Pregunta', questions: [previewQ] } : null}
        lang={lang}
      />
    </div>
  );
}

export function ConfigQuestionnairesTab({ lang }) {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [bankQuestions, setBankQuestions] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'general', question_ids: [] });
  const [showAddQ, setShowAddQ] = useState(false);
  const [filterCat, setFilterCat] = useState('all');
  const [saving, setSaving] = useState(false);

  const lb = lang === 'es'
    ? { title: 'Cuestionarios', new: 'Nuevo Cuestionario', edit: 'Editar Cuestionario', name: 'Nombre', type: 'Tipo', questions: 'Preguntas', save: 'Guardar', cancel: 'Cancelar', no_q: 'No hay cuestionarios configurados', confirm_delete: 'Eliminar este cuestionario?', add_from_bank: 'Añadir del banco', no_questions: 'Sin preguntas', all: 'Todas', no_bank: 'Crea preguntas en el Banco de Preguntas primero' }
    : { title: 'Questionnaires', new: 'New Questionnaire', edit: 'Edit Questionnaire', name: 'Name', type: 'Type', questions: 'Questions', save: 'Save', cancel: 'Cancel', no_q: 'No questionnaires configured', confirm_delete: 'Delete this questionnaire?', add_from_bank: 'Add from bank', no_questions: 'No questions', all: 'All', no_bank: 'Create questions in the Question Bank first' };

  const qTypes = [
    { value: 'general', es: 'General', en: 'General' },
    { value: 'tolerancia', es: 'Tolerancia', en: 'Tolerance' },
    { value: 'eficacia', es: 'Eficacia', en: 'Efficacy' },
    { value: 'seguridad', es: 'Seguridad', en: 'Safety' },
    { value: 'dermatologia', es: 'Dermatología', en: 'Dermatology' },
    // URS-F501: nuevo tipo `eventos_adversos` para que los cuestionarios
    // de config marcados como EA hereden el comportamiento multi-entry
    // + check "No aplica" en el estudio al importarlos.
    { value: 'eventos_adversos', es: 'Eventos Adversos', en: 'Adverse Events' },
    // URS-F503: tipo `medicacion_concomitante` con las mismas features
    // multi-entry (Med1/Med2/...) + check "No aplica" (con texto
    // especifico "Sin cambios desde la ultima visita").
    { value: 'medicacion_concomitante', es: 'Medicación Concomitante', en: 'Concomitant Medication' },
  ];
  const catLabels = { tolerancia: 'Tolerancia', eficacia: 'Eficacia', seguridad: 'Seguridad', dermatologia: 'Dermatología', general: 'General' };
  const catColors = { tolerancia: 'bg-amber-50 text-amber-700 border-amber-200', eficacia: 'bg-green-50 text-green-700 border-green-200', seguridad: 'bg-red-50 text-red-700 border-red-200', dermatologia: 'bg-cyan-50 text-cyan-700 border-cyan-200', general: 'bg-stone-50 text-stone-600 border-stone-200' };

  const load = () => {
    Promise.all([
      axios.get(`${API_URL}/api/config/questionnaires`, { withCredentials: true }),
      axios.get(`${API_URL}/api/config/questions`, { withCredentials: true }),
    ]).then(([qr, br]) => {
      setQuestionnaires(qr.data);
      setBankQuestions(br.data.filter(q => q.active !== false));
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: '', type: 'general', question_ids: [] }); setShowAddQ(false); setShowDialog(true); };
  const openEdit = (q) => { setEditing(q); setForm({ name: q.name, type: q.type, question_ids: (q.questions || []).map(qq => qq.question_id) }); setShowAddQ(false); setShowDialog(true); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(lang === 'es' ? 'El nombre es obligatorio' : 'Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/config/questionnaires/${editing.questionnaire_id}`, form, { withCredentials: true });
        toast.success(lang === 'es' ? 'Cuestionario actualizado' : 'Questionnaire updated');
      } else {
        await axios.post(`${API_URL}/api/config/questionnaires`, form, { withCredentials: true });
        toast.success(lang === 'es' ? 'Cuestionario creado' : 'Questionnaire created');
      }
      setShowDialog(false); load();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const [confirmDeleteCQ, setConfirmDeleteCQ] = useState(null);

  const handleDelete = async (qId) => {
    try { await axios.delete(`${API_URL}/api/config/questionnaires/${qId}`, { withCredentials: true }); toast.success(lang === 'es' ? 'Cuestionario eliminado' : 'Questionnaire deleted'); setConfirmDeleteCQ(null); load(); }
    catch (err) { toast.error(errText(err)); }
  };

  const toggleQuestion = (bq) => {
    setForm(p => {
      const exists = p.question_ids.includes(bq.question_id);
      return { ...p, question_ids: exists ? p.question_ids.filter(id => id !== bq.question_id) : [...p.question_ids, bq.question_id] };
    });
  };

  const removeQuestion = (qid) => { setForm(p => ({ ...p, question_ids: p.question_ids.filter(id => id !== qid) })); };

  // URS-F497: drag-and-drop de las preguntas seleccionadas para reordenar.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleQuestionsDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = parseInt(active.id, 10);
    const newIdx = parseInt(over.id, 10);
    if (Number.isNaN(oldIdx) || Number.isNaN(newIdx)) return;
    setForm(p => ({ ...p, question_ids: arrayMove(p.question_ids, oldIdx, newIdx) }));
  };

  const filteredBank = filterCat === 'all' ? bankQuestions : bankQuestions.filter(q => q.category === filterCat);
  const bankMap = {};
  bankQuestions.forEach(q => { bankMap[q.question_id] = q; });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="create-config-questionnaire-btn" onClick={openCreate} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
          <Plus size={16} />{lb.new}
        </Button>
      </div>

      {questionnaires.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <p className="text-sm text-stone-500">{lb.no_q}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {questionnaires.map(q => (
            <div key={q.questionnaire_id} data-testid={`config-q-card-${q.questionnaire_id}`} className="bg-white border border-stone-200 rounded-sm p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <h3 className="text-sm font-semibold text-stone-900">{q.name}</h3>
                  <Badge variant="outline" className={`text-xs ${catColors[q.type] || catColors.general}`}>{catLabels[q.type] || q.type}</Badge>
                  <Badge variant="outline" className="text-xs font-mono">{(q.questions || []).length} {lang === 'es' ? 'preguntas' : 'questions'}</Badge>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-config-q-${q.questionnaire_id}`} onClick={() => openEdit(q)}><Pencil size={14} /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" data-testid={`delete-config-q-${q.questionnaire_id}`} onClick={() => setConfirmDeleteCQ(q)}><Trash size={14} /></Button>
                </div>
              </div>
              {(q.questions || []).length > 0 && (
                <div className="mt-2 space-y-1">
                  {q.questions.map((qq, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-stone-600 pl-2">
                      <span className="font-mono text-stone-400">{i + 1}.</span>
                      <span>{qq.text_es}</span>
                      <Badge variant="outline" className={`text-[10px] ${catColors[qq.category] || ''}`}>{catLabels[qq.category] || qq.category}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>{editing ? lb.edit : lb.new}</DialogTitle></DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{lb.name} <span className="text-red-500">*</span></Label>
                <Input data-testid="config-q-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>{lb.type}</Label>
                <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger data-testid="config-q-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {qTypes.map(t => (<SelectItem key={t.value} value={t.value}>{lang === 'es' ? t.es : t.en}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">{lb.questions} ({form.question_ids.length})</Label>
                <Button variant="outline" size="sm" data-testid="config-q-add-bank-btn" onClick={() => setShowAddQ(!showAddQ)} className="text-xs gap-1 rounded-sm">
                  <Plus size={12} />{lb.add_from_bank}
                </Button>
              </div>

              {form.question_ids.length === 0 ? (
                <p className="text-xs text-stone-400 text-center py-4">{lb.no_questions}</p>
              ) : (
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleQuestionsDragEnd}>
                  <SortableContext items={form.question_ids.map((_, i) => String(i))} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1 mb-3">
                      {form.question_ids.map((qid, i) => {
                        const bq = bankMap[qid];
                        if (!bq) return null;
                        return (
                          <ConfigSortableRow key={qid} id={String(i)}>
                            {({ attributes, listeners }) => (
                              <div className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-sm px-3 py-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <button
                                    type="button"
                                    {...attributes}
                                    {...listeners}
                                    className="cursor-grab active:cursor-grabbing text-stone-400 hover:text-stone-700 p-0.5 shrink-0"
                                    aria-label={lang === 'es' ? 'Arrastrar para reordenar' : 'Drag to reorder'}
                                    title={lang === 'es' ? 'Arrastrar para reordenar' : 'Drag to reorder'}
                                    data-testid={`config-q-drag-handle-${i}`}
                                  ><DotsSixVertical size={14} weight="bold" /></button>
                                  <span className="font-mono text-xs text-stone-400 shrink-0">{i + 1}.</span>
                                  <span className="text-sm text-stone-800 truncate">{bq.text_es}</span>
                                  <Badge variant="outline" className={`text-[10px] shrink-0 ${catColors[bq.category] || ''}`}>{catLabels[bq.category] || bq.category}</Badge>
                                </div>
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-stone-400 hover:text-red-500 shrink-0" onClick={() => removeQuestion(qid)}><Trash size={12} /></Button>
                              </div>
                            )}
                          </ConfigSortableRow>
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              {showAddQ && (
                <div className="border border-blue-200 bg-blue-50/30 rounded-sm p-3 mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-stone-700">{lang === 'es' ? 'Seleccionar preguntas del banco' : 'Select from question bank'}</p>
                    <Select value={filterCat} onValueChange={setFilterCat}>
                      <SelectTrigger className="w-32 h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{lb.all}</SelectItem>
                        {Object.entries(catLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto space-y-1">
                    {filteredBank.length === 0 ? (
                      <p className="text-xs text-stone-400 text-center py-2">{lb.no_bank}</p>
                    ) : filteredBank.map(bq => (
                      <div key={bq.question_id} className="flex items-center justify-between bg-white border border-stone-200 rounded-sm px-3 py-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-stone-800">{bq.text_es}</span>
                          <Badge variant="outline" className={`text-[10px] ${catColors[bq.category] || ''}`}>{catLabels[bq.category] || bq.category}</Badge>
                        </div>
                        {form.question_ids.includes(bq.question_id) ? (
                          <Check size={14} className="text-green-500" />
                        ) : (
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => toggleQuestion(bq)}><Plus size={12} /></Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="border-t border-stone-200 pt-4">
            <Button variant="ghost" onClick={() => setShowDialog(false)}>{lb.cancel}</Button>
            <Button data-testid="save-config-q-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : lb.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDeleteDialog
        open={!!confirmDeleteCQ}
        onOpenChange={(v) => { if (!v) setConfirmDeleteCQ(null); }}
        title={lang === 'es' ? 'Eliminar cuestionario' : 'Delete questionnaire'}
        description={lang === 'es' ? `¿Estás seguro de que deseas eliminar el cuestionario "${confirmDeleteCQ?.name || ''}"?` : `Are you sure you want to delete questionnaire "${confirmDeleteCQ?.name || ''}"?`}
        onConfirm={() => handleDelete(confirmDeleteCQ?.questionnaire_id)}
      />
    </div>
  );
}

export function ProtocolFieldsTab({ lang }) {
  const [fields, setFields] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: '', type: 'text', options: [] });
  const [newOption, setNewOption] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  // Row-level drag&drop state (drag whole fields in the table) - URS-F180
  const [rowDragIdx, setRowDragIdx] = useState(null);
  const [rowDragOverIdx, setRowDragOverIdx] = useState(null);

  const lb = lang === 'es'
    ? { title: 'Campos del Protocolo', new: 'Nuevo Campo', edit: 'Editar Campo', label: 'Etiqueta', type: 'Tipo', options: 'Opciones', add_option: 'Añadir opcion', save: 'Guardar', cancel: 'Cancelar', no_fields: 'No hay campos de protocolo configurados. Añade los campos que necesites.', confirm_delete: 'Eliminar este campo?', text: 'Texto', select: 'Seleccion', multiselect: 'Multiseleccion' }
    : { title: 'Protocol Fields', new: 'New Field', edit: 'Edit Field', label: 'Label', type: 'Type', options: 'Options', add_option: 'Add option', save: 'Save', cancel: 'Cancel', no_fields: 'No protocol fields configured. Add the fields you need.', confirm_delete: 'Delete this field?', text: 'Text', select: 'Select', multiselect: 'Multi-select' };

  const typeLabels = { text: lb.text, select: lb.select, multiselect: lb.multiselect, counter: lang === 'es' ? 'Contador' : 'Counter' };

  // URS-F183: mapping table Protocol field_id -> Qsalud / Personal Data field
  // Mirrors the backend handlers in study_routes.py::get_eligible_volunteers
  // and recruitment_routes.py. Key matches a prefix of the field_id, so users
  // can recreate a field with the same UUID prefix and the mapping is preserved.
  const QSALUD_MAP = {
    '0d823f16': { source: 'personal', key: 'genero', label_es: 'Genero', label_en: 'Gender' },
    'a924c682': { source: 'personal', key: 'fecha_nacimiento', label_es: 'Fecha de nacimiento', label_en: 'Date of birth' },
    '73b947a4': { source: 'personal', key: 'etnia', label_es: 'Etnia', label_en: 'Ethnicity' },
    '5839840a': { source: 'qsalud', key: 'tipo_piel', label_es: 'Tipo de piel', label_en: 'Skin type' },
    '0ac86515': { source: 'qsalud', key: 'alteraciones_piel', label_es: 'Alteraciones de la piel', label_en: 'Skin alterations' },
    'c75d5e36': { source: 'qsalud', key: 'fitzpatrick', label_es: 'Fototipo Fitzpatrick', label_en: 'Fitzpatrick phototype' },
    '22290dba': { source: 'qsalud', key: 'tipo_cabello', label_es: 'Tipo de cabello', label_en: 'Hair type' },
    '500e7414': { source: 'qsalud', key: 'reactividad_piel', label_es: 'Reactividad de la piel', label_en: 'Skin reactivity' },
  };

  const getMapping = (fieldId) => {
    if (!fieldId) return null;
    for (const prefix in QSALUD_MAP) {
      if (fieldId.startsWith(prefix)) return QSALUD_MAP[prefix];
    }
    return null;
  };

  const load = () => {
    axios.get(`${API_URL}/api/config/protocol-fields`, { withCredentials: true }).then(r => setFields(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ label: '', type: 'text', options: [] }); setNewOption(''); setShowDialog(true); };
  const openEdit = (f) => { setEditing(f); setForm({ label: f.label, type: f.type, options: [...(f.options || [])] }); setNewOption(''); setShowDialog(true); };

  const addOption = () => {
    if (!newOption.trim()) return;
    setForm(p => ({ ...p, options: [...p.options, newOption.trim()] }));
    setNewOption('');
  };
  const removeOption = (i) => { setForm(p => ({ ...p, options: p.options.filter((_, idx) => idx !== i) })); };
  const updateOption = (i, val) => { setForm(p => ({ ...p, options: p.options.map((o, idx) => idx === i ? val : o) })); };
  const moveOption = (i, delta) => {
    setForm(p => {
      const arr = [...p.options];
      const j = i + delta;
      if (j < 0 || j >= arr.length) return p;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...p, options: arr };
    });
  };
  const reorderOption = (from, to) => {
    setForm(p => {
      if (from === to || from < 0 || to < 0 || from >= p.options.length || to >= p.options.length) return p;
      const arr = [...p.options];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return { ...p, options: arr };
    });
  };

  const handleSave = async () => {
    if (!form.label.trim()) { toast.error(lang === 'es' ? 'La etiqueta es obligatoria' : 'Label is required'); return; }
    setSaving(true);
    try {
      const payload = { label: form.label, type: form.type, options: form.options, order: editing ? editing.order : fields.length };
      if (editing) {
        await axios.put(`${API_URL}/api/config/protocol-fields/${editing.field_id}`, payload, { withCredentials: true });
        toast.success(lang === 'es' ? 'Campo actualizado' : 'Field updated');
      } else {
        await axios.post(`${API_URL}/api/config/protocol-fields`, payload, { withCredentials: true });
        toast.success(lang === 'es' ? 'Campo creado' : 'Field created');
      }
      setShowDialog(false); load();
    } catch (err) { toast.error(errText(err)); }
    setSaving(false);
  };

  const [confirmDeletePF, setConfirmDeletePF] = useState(null);

  const handleDelete = async (fId) => {
    try { await axios.delete(`${API_URL}/api/config/protocol-fields/${fId}`, { withCredentials: true }); toast.success(lang === 'es' ? 'Campo eliminado' : 'Field deleted'); setConfirmDeletePF(null); load(); }
    catch (err) { toast.error(errText(err)); }
  };

  // URS-F180: drag&drop reorder of protocol fields (table rows)
  const reorderFields = async (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= fields.length || to >= fields.length) return;
    const next = [...fields];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setFields(next); // optimistic
    try {
      await axios.put(
        `${API_URL}/api/config/protocol-fields-reorder`,
        { order: next.map(f => f.field_id) },
        { withCredentials: true }
      );
    } catch (err) {
      toast.error(errText(err, 'Error al reordenar'));
      load();
    }
  };

  return (
    <div className="space-y-4" data-testid="protocol-fields-tab">
      <div className="flex justify-end">
        <Button data-testid="new-protocol-field-btn" onClick={openCreate} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
          <Plus size={16} />{lb.new}
        </Button>
      </div>

      {fields.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <p className="text-sm text-stone-500">{lb.no_fields}</p>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead className="w-8">#</TableHead>
                <TableHead>{lb.label}</TableHead>
                <TableHead className="w-32">{lb.type}</TableHead>
                <TableHead>{lb.options}</TableHead>
                <TableHead className="w-48">{lang === 'es' ? 'Machea con' : 'Maps to'}</TableHead>
                <TableHead className="w-20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map((f, i) => (
                <TableRow
                  key={f.field_id}
                  data-testid={`protocol-field-row-${f.field_id}`}
                  draggable
                  onDragStart={(e) => {
                    setRowDragIdx(i);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', String(i));
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (rowDragOverIdx !== i) setRowDragOverIdx(i);
                  }}
                  onDragLeave={() => {
                    if (rowDragOverIdx === i) setRowDragOverIdx(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = Number(e.dataTransfer.getData('text/plain'));
                    if (Number.isFinite(from) && from !== i) reorderFields(from, i);
                    setRowDragIdx(null);
                    setRowDragOverIdx(null);
                  }}
                  onDragEnd={() => { setRowDragIdx(null); setRowDragOverIdx(null); }}
                  className={`transition-colors ${rowDragIdx === i ? 'opacity-40' : ''} ${rowDragOverIdx === i && rowDragIdx !== i ? 'bg-[#1f3e80]/5 ring-1 ring-[#1f3e80]/40' : ''}`}
                >
                  <TableCell className="cursor-grab active:cursor-grabbing text-stone-300 hover:text-[#1f3e80]" data-testid={`protocol-field-drag-handle-${f.field_id}`} title={lang === 'es' ? 'Arrastrar para reordenar' : 'Drag to reorder'}>
                    <DotsSixVertical size={16} />
                  </TableCell>
                  <TableCell className="font-mono text-xs text-stone-400">{i + 1}</TableCell>
                  <TableCell className="font-medium text-sm text-stone-800">{f.label}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{typeLabels[f.type] || f.type}</Badge></TableCell>
                  <TableCell>
                    {(f.options || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {f.options.map((o, oi) => (
                          <span key={oi} className="text-xs bg-stone-100 border border-stone-200 rounded-full px-2 py-0.5 text-stone-600">{o}</span>
                        ))}
                      </div>
                    ) : <span className="text-xs text-stone-400">—</span>}
                  </TableCell>
                  <TableCell data-testid={`protocol-field-mapping-${f.field_id}`}>
                    {(() => {
                      const m = getMapping(f.field_id);
                      if (!m) return <span className="text-xs text-stone-300" title={lang === 'es' ? 'Sin mapeo automatico con datos del voluntario' : 'No automatic mapping with volunteer data'}>—</span>;
                      const label = lang === 'es' ? m.label_es : m.label_en;
                      const sourceLabel = m.source === 'qsalud' ? 'Qsalud' : (lang === 'es' ? 'Datos personales' : 'Personal data');
                      const color = m.source === 'qsalud' ? 'bg-[#1f3e80]/10 text-[#1f3e80] border-[#1f3e80]/30' : 'bg-emerald-50 text-emerald-700 border-emerald-200';
                      return (
                        <div className="flex flex-col gap-0.5" title={lang === 'es' ? `Matching tolerante a tildes/mayusculas (URS-F182)` : 'Tilde/case-insensitive matching (URS-F182)'}>
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border w-fit ${color}`}>{sourceLabel}</span>
                          <span className="text-xs text-stone-700 font-medium">{label}</span>
                          <span className="text-[10px] text-stone-400 font-mono">{m.key}</span>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-protocol-field-${f.field_id}`} onClick={() => openEdit(f)}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" data-testid={`delete-protocol-field-${f.field_id}`} onClick={() => setConfirmDeletePF(f)}><Trash size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? lb.edit : lb.new}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <Label>{lb.label} <span className="text-red-500">*</span></Label>
              <Input data-testid="protocol-field-label-input" value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>{lb.type}</Label>
              <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                <SelectTrigger data-testid="protocol-field-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">{lb.text}</SelectItem>
                  <SelectItem value="select">{lb.select}</SelectItem>
                  <SelectItem value="multiselect">{lb.multiselect}</SelectItem>
                  <SelectItem value="counter">{lang === 'es' ? 'Contador' : 'Counter'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {['select', 'multiselect'].includes(form.type) && (
              <div className="space-y-2">
                <Label>{lb.options}</Label>
                <div className="space-y-1">
                  {form.options.map((opt, i) => (
                    <div
                      key={i}
                      draggable
                      onDragStart={(e) => { setDragIdx(i); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); }}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverIdx !== i) setDragOverIdx(i); }}
                      onDragLeave={() => { if (dragOverIdx === i) setDragOverIdx(null); }}
                      onDrop={(e) => { e.preventDefault(); const from = Number(e.dataTransfer.getData('text/plain')); if (Number.isFinite(from) && from !== i) reorderOption(from, i); setDragIdx(null); setDragOverIdx(null); }}
                      onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                      className={`flex items-center gap-1 rounded-sm transition-colors ${dragIdx === i ? 'opacity-40' : ''} ${dragOverIdx === i && dragIdx !== i ? 'bg-[#1f3e80]/5 ring-1 ring-[#1f3e80]/40' : ''}`}
                      data-testid={`protocol-option-row-${i}`}
                    >
                      <span className="cursor-grab active:cursor-grabbing text-stone-300 hover:text-[#1f3e80] shrink-0 px-0.5" title={lang === 'es' ? 'Arrastrar para reordenar' : 'Drag to reorder'}>
                        <DotsSixVertical size={14} />
                      </span>
                      <div className="flex flex-col -gap-px shrink-0">
                        <Button variant="ghost" size="icon" className="h-4 w-6 text-stone-400 hover:text-[#1f3e80]"
                          onClick={() => moveOption(i, -1)} disabled={i === 0}
                          data-testid={`protocol-option-up-${i}`} title={lang === 'es' ? 'Subir' : 'Move up'}>
                          <CaretUp size={11} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-4 w-6 text-stone-400 hover:text-[#1f3e80]"
                          onClick={() => moveOption(i, 1)} disabled={i === form.options.length - 1}
                          data-testid={`protocol-option-down-${i}`} title={lang === 'es' ? 'Bajar' : 'Move down'}>
                          <CaretDown size={11} />
                        </Button>
                      </div>
                      <span className="text-[10px] font-mono text-stone-300 w-5 text-right">{i + 1}.</span>
                      <Input value={opt} onChange={e => updateOption(i, e.target.value)} className="flex-1 h-8 text-sm" data-testid={`protocol-option-${i}`} />
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 shrink-0" onClick={() => removeOption(i)}><Trash size={12} /></Button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input data-testid="protocol-new-option-input" value={newOption} onChange={e => setNewOption(e.target.value)} placeholder={lang === 'es' ? 'Nueva opcion...' : 'New option...'} className="flex-1 h-8 text-sm"
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }} />
                  <Button variant="outline" size="sm" className="h-8 rounded-sm gap-1" data-testid="protocol-add-option-btn" onClick={addOption}><Plus size={12} />{lb.add_option}</Button>
                </div>
              </div>
            )}
            {editing && ['select', 'multiselect'].includes(form.type) && getMapping(editing.field_id) && (
              <QsaludComparisonPanel
                mapping={getMapping(editing.field_id)}
                currentOptions={form.options}
                onImport={(vals) => setForm(p => ({
                  ...p,
                  options: [...p.options, ...vals.filter(v => !p.options.some(o => normalizeMatch(o) === normalizeMatch(v)))],
                }))}
                lang={lang}
              />
            )}
          </div>
          <DialogFooter className="border-t border-stone-200 pt-4">
            <Button variant="ghost" onClick={() => setShowDialog(false)}>{lb.cancel}</Button>
            <Button data-testid="save-protocol-field-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : lb.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDeleteDialog
        open={!!confirmDeletePF}
        onOpenChange={(v) => { if (!v) setConfirmDeletePF(null); }}
        title={lang === 'es' ? 'Eliminar campo' : 'Delete field'}
        description={lang === 'es' ? `¿Estás seguro de que deseas eliminar el campo "${confirmDeletePF?.label || ''}"?` : `Are you sure you want to delete field "${confirmDeletePF?.label || ''}"?`}
        onConfirm={() => handleDelete(confirmDeletePF?.field_id)}
      />
    </div>
  );
}

// URS-F184: Helper to normalize strings for case+accent insensitive matching.
// Mirrors the backend logic in study_routes.py::_accent_insensitive_pattern
// so the comparison panel highlights matches consistently with the server.
function normalizeMatch(s) {
  if (s === null || s === undefined) return '';
  return String(s).trim().toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
}

// URS-F184: Real-time comparison panel between the Protocol field options
// being edited and the actual values populated in the volunteers BD for the
// mapped Qsalud / personal_data field. Allows one-click import of missing
// values into the Protocol options.
export function QsaludComparisonPanel({ mapping, currentOptions, onImport, lang }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!mapping) return;
    setLoading(true);
    axios.get(`${API_URL}/api/config/qsalud-options`, {
      params: { key: mapping.key, source: mapping.source },
      withCredentials: true,
    })
      .then(({ data }) => setData(data))
      .catch(() => toast.error(lang === 'es' ? 'Error al cargar opciones del Qsalud' : 'Error loading Qsalud options'))
      .finally(() => setLoading(false));
  }, [mapping, lang]);

  if (loading) {
    return (
      <div className="border-t border-stone-200 pt-4 text-xs text-stone-400 text-center" data-testid="qsalud-panel-loading">
        {lang === 'es' ? 'Cargando opciones del Qsalud...' : 'Loading Qsalud options...'}
      </div>
    );
  }
  if (!data) return null;

  const currentNorm = new Set(currentOptions.map(normalizeMatch));
  const bdValues = data.bd_values || [];
  const inBdNorm = new Set(bdValues.map(b => normalizeMatch(b.value)));

  const missingInProtocol = bdValues.filter(b => !currentNorm.has(normalizeMatch(b.value)));
  const onlyInProtocol = currentOptions.filter(o => o && !inBdNorm.has(normalizeMatch(o)));
  const matched = bdValues.filter(b => currentNorm.has(normalizeMatch(b.value)));

  const sourceLabel = mapping.source === 'qsalud' ? 'Qsalud' : (lang === 'es' ? 'Datos personales' : 'Personal data');

  return (
    <div className="border-t border-stone-200 pt-4 space-y-3" data-testid="qsalud-comparison-panel">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-stone-700">
            {lang === 'es' ? 'Comparativa con' : 'Comparison with'} {sourceLabel}: <span className="font-mono text-[#1f3e80]">{mapping.key}</span>
          </p>
          <p className="text-[10px] text-stone-400">
            {lang === 'es'
              ? `${matched.length} coincidencias · ${missingInProtocol.length} solo en BD · ${onlyInProtocol.length} solo en Protocolo`
              : `${matched.length} matches · ${missingInProtocol.length} only in DB · ${onlyInProtocol.length} only in Protocol`}
          </p>
        </div>
        {missingInProtocol.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs rounded-sm gap-1 border-[#1f3e80] text-[#1f3e80] hover:bg-[#1f3e80]/5"
            data-testid="qsalud-import-missing-btn"
            onClick={() => onImport(missingInProtocol.map(b => b.value))}
          >
            <Plus size={12} />
            {lang === 'es' ? `Importar ${missingInProtocol.length} que faltan` : `Import ${missingInProtocol.length} missing`}
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="border border-stone-200 rounded-sm bg-stone-50 p-2 space-y-1">
          <p className="text-[10px] font-semibold uppercase text-stone-500 tracking-wide">
            {lang === 'es' ? 'Valores en BD (con conteo)' : 'Values in DB (with count)'}
          </p>
          {bdValues.length === 0 ? (
            <p className="text-xs text-stone-400 italic">{lang === 'es' ? 'Sin valores en BD aún' : 'No values in DB yet'}</p>
          ) : (
            bdValues.map((b, i) => {
              const isMatched = currentNorm.has(normalizeMatch(b.value));
              return (
                <div key={i} className="flex items-center justify-between text-xs" data-testid={`qsalud-bd-value-${i}`}>
                  <span className={`flex items-center gap-1 ${isMatched ? 'text-emerald-700' : 'text-amber-700'}`}>
                    <span className={`inline-block w-3 h-3 rounded-full text-center text-[8px] leading-[12px] font-bold ${isMatched ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {isMatched ? '✓' : '!'}
                    </span>
                    <span>{b.value}</span>
                  </span>
                  <span className="font-mono text-[10px] text-stone-400">{b.count}</span>
                </div>
              );
            })
          )}
        </div>
        <div className="border border-stone-200 rounded-sm bg-stone-50 p-2 space-y-1">
          <p className="text-[10px] font-semibold uppercase text-stone-500 tracking-wide">
            {lang === 'es' ? 'Opciones solo en Protocolo' : 'Options only in Protocol'}
          </p>
          {onlyInProtocol.length === 0 ? (
            <p className="text-xs text-stone-400 italic">{lang === 'es' ? 'Todas las opciones machean con BD' : 'All options match BD'}</p>
          ) : (
            onlyInProtocol.map((o, i) => (
              <div key={i} className="flex items-center gap-1 text-xs text-stone-600" data-testid={`qsalud-only-protocol-${i}`}>
                <span className="inline-block w-3 h-3 rounded-full text-center text-[8px] leading-[12px] font-bold bg-stone-200 text-stone-600">·</span>
                <span>{o}</span>
              </div>
            ))
          )}
        </div>
      </div>
      {data.canonical && data.canonical.length > 0 && (
        <details className="text-xs text-stone-500">
          <summary className="cursor-pointer hover:text-[#1f3e80]">
            {lang === 'es' ? `Opciones canonicas en Configuracion Qsalud (${data.canonical.length})` : `Canonical options in Qsalud config (${data.canonical.length})`}
          </summary>
          <div className="mt-2 flex flex-wrap gap-1">
            {data.canonical.map((c, i) => (
              <span key={i} className="text-[10px] bg-white border border-stone-200 rounded-full px-2 py-0.5 text-stone-500">{c}</span>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

export function TemplatesTab({ lang }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [generating, setGenerating] = useState(null);

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/consent-templates?include_inactive=true`, { withCredentials: true });
      setTemplates(data);
    } catch (e) { toast.error('Error al cargar plantillas'); }
    setLoading(false);
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handlePreview = async (t) => {
    setGenerating(t.template_id);
    try {
      const resp = await axios.get(`${API_URL}/api/consent-templates/${t.template_id}/preview`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) { toast.error('Error al previsualizar'); }
    setGenerating(null);
  };

  const handleSave = async (templateData) => {
    try {
      if (templateData.template_id) {
        await axios.put(`${API_URL}/api/consent-templates/${templateData.template_id}`, templateData, { withCredentials: true });
        toast.success(lang === 'es' ? 'Plantilla actualizada' : 'Template updated');
      } else {
        await axios.post(`${API_URL}/api/consent-templates`, templateData, { withCredentials: true });
        toast.success(lang === 'es' ? 'Plantilla creada' : 'Template created');
      }
      setShowEditor(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (e) { toast.error('Error al guardar'); }
  };

  const handleDelete = async (t) => {
    const msg = lang === 'es'
      ? `¿Eliminar la plantilla "${t.name}"?\n\nEsta accion no se puede deshacer. Los consentimientos ya firmados con esta plantilla no se veran afectados.`
      : `Delete template "${t.name}"?\n\nThis cannot be undone. Already-signed consents are not affected.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${API_URL}/api/consent-templates/${t.template_id}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Plantilla eliminada' : 'Template deleted');
      fetchTemplates();
    } catch (e) {
      toast.error(lang === 'es' ? 'Error al eliminar la plantilla' : 'Error deleting template');
    }
  };

  if (loading) return <div className="text-center py-8 text-stone-400">Cargando...</div>;

  return (
    <div className="space-y-4" data-testid="templates-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-stone-800">{lang === 'es' ? 'Plantillas de Consentimiento' : 'Consent Templates'}</h3>
          <p className="text-xs text-stone-500">{lang === 'es' ? 'Gestiona el texto legal de las plantillas de LOPD, imagenes y otros consentimientos. La generacion de PDFs se realiza desde la pestana Documentos de cada voluntario.' : 'Manage the legal text of LOPD, image and other consent templates. PDF generation is done from each volunteer\'s Documents tab.'}</p>
        </div>
        <Button size="sm" onClick={() => { setEditingTemplate(null); setShowEditor(true); }} data-testid="add-template-btn">
          <Plus size={14} className="mr-1" /> {lang === 'es' ? 'Nueva plantilla' : 'New template'}
        </Button>
      </div>

      <div className="grid gap-4">
        {templates.map(t => (
          <div key={t.template_id} className="bg-white border border-stone-200 rounded-sm p-4" data-testid={`template-card-${t.type}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <FileText size={18} className="text-emerald-700" />
                  <span className="font-semibold text-stone-800 text-sm">{t.name}</span>
                  <Badge variant="outline" className="text-[10px]">{t.type.toUpperCase()}</Badge>
                </div>
                <p className="text-xs text-stone-500 ml-6">{t.title}</p>
                <p className="text-[10px] text-stone-400 ml-6 mt-1">
                  {t.sections?.length || 0} {lang === 'es' ? 'secciones' : 'sections'} · {t.clauses?.length || 0} {lang === 'es' ? 'clausulas' : 'clauses'}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => handlePreview(t)} disabled={generating === t.template_id} data-testid={`preview-template-${t.type}`}>
                  <FileText size={14} className="mr-1" /> {lang === 'es' ? 'Vista previa' : 'Preview'}
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingTemplate(t); setShowEditor(true); }} data-testid={`edit-template-${t.type}`}>
                  <Pencil size={14} />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(t)} data-testid={`delete-template-${t.template_id}`} title={lang === 'es' ? 'Eliminar plantilla' : 'Delete template'}>
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
            <FileText size={40} className="mx-auto text-stone-300 mb-3" />
            <p className="text-sm text-stone-500">{lang === 'es' ? 'No hay plantillas. Crea una nueva.' : 'No templates. Create a new one.'}</p>
          </div>
        )}
      </div>

      {/* Template Editor Dialog */}
      {showEditor && (
        <ConsentTemplateEditor
          lang={lang}
          template={editingTemplate}
          onSave={handleSave}
          onClose={() => { setShowEditor(false); setEditingTemplate(null); }}
        />
      )}
    </div>
  );
}


export function ConsentTemplateEditor({ lang, template, onSave, onClose }) {
  const [form, setForm] = useState({
    template_id: template?.template_id || '',
    name: template?.name || '',
    type: template?.type || 'custom',
    title: template?.title || '',
    accent_color: template?.accent_color || '#2C5545',
    sections: template?.sections || [{ key: '', label: '', content: '' }],
    clauses: template?.clauses || [],
    company_info: template?.company_info || { name: '', address: '', email: '' },
    rights_text: template?.rights_text || '',
    contact_info: template?.contact_info || '',
  });

  const updateSection = (idx, field, value) => {
    const s = [...form.sections];
    s[idx] = { ...s[idx], [field]: value };
    setForm({ ...form, sections: s });
  };

  const addSection = () => {
    setForm({ ...form, sections: [...form.sections, { key: '', label: '', content: '' }] });
  };

  const removeSection = (idx) => {
    setForm({ ...form, sections: form.sections.filter((_, i) => i !== idx) });
  };

  const addClause = () => {
    setForm({ ...form, clauses: [...form.clauses, { clause_id: Date.now().toString(), text: '', required: true }] });
  };

  const updateClause = (idx, field, value) => {
    const c = [...form.clauses];
    c[idx] = { ...c[idx], [field]: value };
    setForm({ ...form, clauses: c });
  };

  const removeClause = (idx) => {
    setForm({ ...form, clauses: form.clauses.filter((_, i) => i !== idx) });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{template ? (lang === 'es' ? 'Editar plantilla' : 'Edit template') : (lang === 'es' ? 'Nueva plantilla' : 'New template')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Basic info */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>{lang === 'es' ? 'Nombre' : 'Name'}</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="template-name-input" />
            </div>
            <div>
              <Label>{lang === 'es' ? 'Tipo' : 'Type'}</Label>
              <Input value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} data-testid="template-type-input" />
            </div>
            <div>
              <Label>{lang === 'es' ? 'Empresa' : 'Company'}</Label>
              <Input value={form.company_info.name} onChange={e => setForm({ ...form, company_info: { ...form.company_info, name: e.target.value } })} />
            </div>
          </div>

          <div className="grid grid-cols-[1fr_180px] gap-3">
            <div>
              <Label>{lang === 'es' ? 'Titulo del documento' : 'Document title'}</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="template-title-input" />
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                <Palette size={12} weight="bold" />
                {lang === 'es' ? 'Color de acento' : 'Accent color'}
              </Label>
              <div className="flex items-center gap-1.5 mt-1">
                <input type="color" value={form.accent_color}
                  onChange={e => setForm({ ...form, accent_color: e.target.value })}
                  className="h-9 w-12 rounded-sm border border-stone-200 cursor-pointer p-0"
                  data-testid="template-accent-color-picker" />
                <input type="text" value={form.accent_color}
                  onChange={e => setForm({ ...form, accent_color: e.target.value })}
                  className="h-9 flex-1 text-xs font-mono border border-stone-200 rounded-sm px-2"
                  data-testid="template-accent-color-text" />
              </div>
              <p className="text-[10px] text-stone-400 mt-1">
                {lang === 'es' ? 'Titulo y secciones del PDF' : 'PDF title & section labels'}
              </p>
            </div>
          </div>

          {/* Sections */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">{lang === 'es' ? 'Secciones del texto legal' : 'Legal text sections'}</Label>
              <Button size="sm" variant="outline" onClick={addSection} data-testid="add-section-btn"><Plus size={12} className="mr-1" /> {lang === 'es' ? 'Seccion' : 'Section'}</Button>
            </div>
            {form.sections.map((s, i) => (
              <div key={i} className="border border-stone-200 rounded-sm p-3 mb-2 bg-stone-50">
                <div className="flex items-center gap-2 mb-2">
                  <Input placeholder={lang === 'es' ? 'Etiqueta (ej: Responsable del tratamiento)' : 'Label'} value={s.label} onChange={e => updateSection(i, 'label', e.target.value)} className="flex-1 text-sm" />
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeSection(i)}><Trash size={12} /></Button>
                </div>
                <RichTextField
                  value={s.content}
                  onChange={(v) => updateSection(i, 'content', v)}
                  placeholder={lang === 'es' ? 'Contenido del texto legal... usa el toolbar para Negrita / Cursiva / Subrayado / Color' : 'Legal text content... use the toolbar for Bold / Italic / Underline / Color'}
                  multiline
                  minRows={4}
                  testId={`section-content-${i}`}
                  lang={lang}
                />
              </div>
            ))}
          </div>

          {/* Clauses */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">{lang === 'es' ? 'Clausulas de consentimiento (checkbox)' : 'Consent clauses (checkbox)'}</Label>
              <Button size="sm" variant="outline" onClick={addClause} data-testid="add-clause-btn"><Plus size={12} className="mr-1" /> {lang === 'es' ? 'Clausula' : 'Clause'}</Button>
            </div>
            {form.clauses.map((c, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <Check size={14} className="text-emerald-600 flex-shrink-0" />
                <Input placeholder={lang === 'es' ? 'Texto de la clausula...' : 'Clause text...'} value={c.text} onChange={e => updateClause(i, 'text', e.target.value)} className="flex-1 text-sm" />
                <div className="flex items-center gap-1 text-xs text-stone-500">
                  <Switch checked={c.required} onCheckedChange={v => updateClause(i, 'required', v)} />
                  <span>{lang === 'es' ? 'Oblig.' : 'Req.'}</span>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeClause(i)}><Trash size={12} /></Button>
              </div>
            ))}
          </div>

          {/* VIDsigner: signature box position */}
          <div className="bg-violet-50/40 border border-violet-200 rounded-sm p-3 space-y-2">
            <div className="flex items-start gap-2">
              <FingerprintSimple size={18} className="text-violet-700 mt-0.5" weight="bold" />
              <div>
                <Label className="text-sm font-semibold text-violet-900">{lang === 'es' ? 'Configuracion VIDsigner' : 'VIDsigner setup'}</Label>
                <p className="text-[11px] text-stone-600 mt-1 leading-relaxed">
                  {lang === 'es' ? (
                    <>
                      <b>Posicion de la firma y de los checks de las clausulas:</b> automatica.<br />
                      Se detectan en el PDF justo debajo de <b>"Firma:"</b> y sobre los <b>"[ ] SI"</b> de cada clausula.
                      No es necesario configurar coordenadas a mano.
                    </>
                  ) : (
                    <>
                      <b>Signature and clause-check positions:</b> automatic.<br />
                      They are detected in the PDF below <b>"Firma:"</b> and on top of each clause's <b>"[ ] YES"</b>.
                      No manual coordinates needed.
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Rights and Contact */}
          <div>
            <Label className="text-sm font-semibold">{lang === 'es' ? 'Derechos del interesado' : 'Data subject rights'}</Label>
            <div className="mt-1">
              <RichTextField
                value={form.rights_text}
                onChange={(v) => setForm({ ...form, rights_text: v })}
                placeholder={lang === 'es' ? 'Derecho a retirar el consentimiento...\nDerecho de acceso...' : 'Right to withdraw consent...\nRight of access...'}
                multiline
                minRows={3}
                testId="rights-text-input"
                lang={lang}
              />
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold">{lang === 'es' ? 'Datos de contacto (DPD)' : 'Contact information (DPO)'}</Label>
            <div className="mt-1">
              <RichTextField
                value={form.contact_info}
                onChange={(v) => setForm({ ...form, contact_info: v })}
                placeholder={lang === 'es' ? 'Direccion, email del DPD...' : 'Address, DPO email...'}
                multiline
                minRows={3}
                testId="contact-info-input"
                lang={lang}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
          <Button onClick={() => onSave(form)} data-testid="save-template-btn">{lang === 'es' ? 'Guardar' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ValidationTab({ lang }) {
  const [downloading, setDownloading] = useState(null);

  const docs = [
    {
      id: 'urs',
      code: 'URS-ECRD-001',
      title: lang === 'es' ? 'Especificacion de Requisitos de Usuario (URS)' : 'User Requirements Specification (URS)',
      desc: lang === 'es'
        ? 'Documento completo con todos los requisitos funcionales, regulatorios y no funcionales del sistema. Incluye roles, modulos, requisitos 21 CFR Part 11 y ALCOA+.'
        : 'Complete document with all functional, regulatory and non-functional system requirements. Includes roles, modules, 21 CFR Part 11 and ALCOA+ requirements.',
      endpoint: '/api/validation/urs',
      filename: 'URS-ECRD-001.pdf',
      supportsRedacted: true,  // URS-F312
    },
    {
      id: 'traceability',
      code: 'TM-ECRD-001',
      title: lang === 'es' ? 'Matriz de Trazabilidad' : 'Traceability Matrix',
      desc: lang === 'es'
        ? 'Mapeo completo de requisitos a funcionalidades, pruebas ejecutadas y resultados. Incluye resumen de las 12 iteraciones de testing con tasa de exito del 100%.'
        : 'Complete mapping of requirements to features, executed tests and results. Includes summary of 12 testing iterations with 100% success rate.',
      endpoint: '/api/validation/traceability',
      filename: 'TM-ECRD-001.pdf',
      supportsRedacted: true,  // URS-F312
    },
    {
      id: 'plan',
      code: 'VP-ECRD-001',
      title: lang === 'es' ? 'Plan de Validacion (VP)' : 'Validation Plan (VP)',
      desc: lang === 'es'
        ? 'Estrategia completa de validacion CSV: alcance, categoria GAMP5, cualificaciones IQ/OQ/PQ, criterios de aceptacion, roles, control de cambios y calendario.'
        : 'Complete CSV validation strategy: scope, GAMP5 category, IQ/OQ/PQ qualifications, acceptance criteria, roles, change control and schedule.',
      endpoint: '/api/validation/plan',
      filename: 'VP-ECRD-001.pdf',
    },
    {
      id: 'cfr11',
      code: 'CFR11-ECRD-001',
      title: lang === 'es' ? 'Declaracion de Conformidad 21 CFR Part 11' : '21 CFR Part 11 Conformity Declaration',
      desc: lang === 'es'
        ? 'Detalle seccion por seccion del cumplimiento de 21 CFR Part 11: Subparte B (registros electronicos, audit trail, 11.10 a-k), Subparte C (firmas electronicas, 11.100-11.300). Incluye datos en vivo del sistema.'
        : 'Section-by-section 21 CFR Part 11 compliance detail: Subpart B (electronic records, audit trail, 11.10 a-k), Subpart C (electronic signatures, 11.100-11.300). Includes live system data.',
      endpoint: '/api/validation/cfr11',
      filename: 'CFR11-ECRD-001.pdf',
    },
    {
      id: 'pq-report',
      code: 'PQ-Report-ECRD-001',
      title: lang === 'es' ? 'Informe de Cualificacion de Rendimiento (PQ)' : 'Performance Qualification Report (PQ)',
      desc: lang === 'es'
        ? 'Informe formal de Fase 4 (PQ). Demuestra con datos REALES en vivo (voluntarios, estudios, firmas, audit, tickets) que el sistema rinde adecuadamente en produccion. Embebe la firma electronica de cierre de Fase 4 si esta ya esta firmada.'
        : 'Formal Phase 4 (PQ) report. Demonstrates with live REAL data (volunteers, studies, signatures, audit, tickets) that the system performs adequately in production. Embeds Phase 4 closure e-signature if already signed.',
      endpoint: '/api/validation/pq-report',
      filename: 'PQ-Report-ECRD-001.pdf',
    },
    {
      id: 'final-report',
      code: 'VR-ECRD-001',
      title: lang === 'es' ? 'Informe Final de Validacion (VR)' : 'Final Validation Report (VR)',
      desc: lang === 'es'
        ? 'Informe consolidado que cierra las Fases 5 y 6. Resume IQ + OQ + PQ y certifica que el sistema queda VALIDADO para uso productivo. Incluye las firmas electronicas de cierre de las 6 fases.'
        : 'Consolidated report closing Phases 5 and 6. Summarizes IQ + OQ + PQ and certifies the system as VALIDATED for production use. Includes e-signatures for the 6 phases.',
      endpoint: '/api/validation/final-report',
      filename: 'VR-ECRD-001.pdf',
    },
  ];

  const sops = [
    {
      id: 'sop-001',
      code: 'SOP-ECRD-001',
      title: lang === 'es' ? 'Gestion de Usuarios y Control de Acceso' : 'User Management & Access Control',
      desc: lang === 'es'
        ? 'Alta, modificacion y baja de usuarios. Asignacion de roles (multi-rol). Configuracion de PIN de firma. Cambio de contrasena. Mi Perfil.'
        : 'User creation, modification and deletion. Role assignment (multi-role). Signature PIN setup. Password change. My Profile.',
      endpoint: '/api/validation/sop/sop-001',
      filename: 'SOP-ECRD-001.pdf',
    },
    {
      id: 'sop-002',
      code: 'SOP-ECRD-002',
      title: lang === 'es' ? 'Gestion de Voluntarios' : 'Volunteer Management',
      desc: lang === 'es'
        ? 'Alta y datos personales. Cuestionario de salud. Consentimientos (firma y archivado). Asignacion a estudios. Proceso de inclusion/exclusion.'
        : 'Registration and personal data. Health questionnaire. Consents (signature and archival). Study assignment. Inclusion/exclusion process.',
      endpoint: '/api/validation/sop/sop-002',
      filename: 'SOP-ECRD-002.pdf',
    },
    {
      id: 'sop-003',
      code: 'SOP-ECRD-003',
      title: lang === 'es' ? 'Gestion de Estudios Clinicos' : 'Clinical Study Management',
      desc: lang === 'es'
        ? 'Creacion y configuracion de estudios. Protocolo, visitas y cuestionarios. Asignacion de personal. Dashboard del estudio.'
        : 'Study creation and configuration. Protocol, visits and questionnaires. Staff assignment. Study dashboard.',
      endpoint: '/api/validation/sop/sop-003',
      filename: 'SOP-ECRD-003.pdf',
    },
    {
      id: 'sop-004',
      code: 'SOP-ECRD-004',
      title: lang === 'es' ? 'Firma Electronica - 21 CFR Part 11' : 'Electronic Signature - 21 CFR Part 11',
      desc: lang === 'es'
        ? 'Configuracion del PIN. Acciones que requieren firma. Proceso de firma. Registros de auditoria. Seguridad y responsabilidades.'
        : 'PIN setup. Actions requiring signature. Signing process. Audit records. Security and responsibilities.',
      endpoint: '/api/validation/sop/sop-004',
      filename: 'SOP-ECRD-004.pdf',
    },
    {
      id: 'sop-005',
      code: 'SOP-ECRD-005',
      title: lang === 'es' ? 'Almacen y Trazabilidad de Muestras' : 'Warehouse & Sample Traceability',
      desc: lang === 'es'
        ? 'Recepcion de muestras. Dispensacion con trazabilidad. Movimientos (devolucion/destruccion). Temperatura. Sondas y pesos.'
        : 'Sample reception. Dispensation with traceability. Movements (return/destruction). Temperature. Probes and weights.',
      endpoint: '/api/validation/sop/sop-005',
      filename: 'SOP-ECRD-005.pdf',
    },
    {
      id: 'sop-006',
      code: 'SOP-ECRD-006',
      title: lang === 'es' ? 'Auditoria y Trazabilidad ALCOA+' : 'Audit & ALCOA+ Traceability',
      desc: lang === 'es'
        ? 'Principios ALCOA+. Contenido de registros. Filtros y detalle. Diff visual. Exportacion CSV. Inmutabilidad. Revision periodica.'
        : 'ALCOA+ principles. Record content. Filters and detail. Visual diff. CSV export. Immutability. Periodic review.',
      endpoint: '/api/validation/sop/sop-006',
      filename: 'SOP-ECRD-006.pdf',
    },
  ];

  const manualDoc = {
    id: 'manual',
    code: 'MANUAL-USR',
    title: lang === 'es' ? 'Manual de Usuario' : 'User Manual',
    description: lang === 'es' ? 'Guia completa de uso del sistema eCRD con capturas de pantalla' : 'Complete eCRD system user guide with screenshots',
    endpoint: '/api/validation/manual',
    filename: 'Manual_Usuario_eCRD.pdf',
  };

  const evidenceDoc = {
    id: 'evidence',
    code: 'IQ-OQ-ECRD-001',
    title: lang === 'es' ? 'Informe de Evidencias IQ/OQ' : 'IQ/OQ Evidence Report',
    description: lang === 'es'
      ? 'Informe automatizado con capturas Playwright por cada requisito URS. Ejecuta tests en vivo y documenta resultado con screenshot como evidencia objetiva para auditoria.'
      : 'Automated report with Playwright screenshots for each URS requirement. Runs live tests and documents results with screenshots as objective audit evidence.',
  };

  const [evidenceGenerating, setEvidenceGenerating] = useState(false);
  const [evidenceReady, setEvidenceReady] = useState(false);
  const [evidenceError, setEvidenceError] = useState(null);
  const [evidenceElapsed, setEvidenceElapsed] = useState(null);
  // URS-F227: live progress {current, total, percent, urs}
  const [evidenceProgress, setEvidenceProgress] = useState({ current: 0, total: 0, percent: 0, urs: null });
  // URS-F230: redacted mode toggle (blur all PII for external auditors / clients)
  const [evidenceRedacted, setEvidenceRedacted] = useState(false);
  // Currently-ready variant on the server (so download requests the right file)
  const [evidenceReadyVariant, setEvidenceReadyVariant] = useState(null); // null | 'full' | 'redacted'

  const startEvidence = async () => {
    setEvidenceGenerating(true);
    setEvidenceReady(false);
    setEvidenceError(null);
    setEvidenceElapsed(0);
    setEvidenceProgress({ current: 0, total: 0, percent: 0, urs: null });
    try {
      await axios.post(`${API_URL}/api/validation/evidence/generate`, { redacted: evidenceRedacted }, { withCredentials: true });
      pollEvidence();
    } catch (err) {
      setEvidenceGenerating(false);
      setEvidenceError(errText(err, 'Error'));
      toast.error('Error al iniciar generacion');
    }
  };

  const resetEvidence = async () => {
    try {
      await axios.post(`${API_URL}/api/validation/evidence/reset`, {}, { withCredentials: true });
      setEvidenceGenerating(false);
      setEvidenceError(lang === 'es' ? 'Estado reseteado manualmente. Puede lanzar una nueva generacion.' : 'State reset manually.');
      toast.success(lang === 'es' ? 'Estado reseteado' : 'State reset');
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const pollEvidence = () => {
    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/validation/evidence/status`, { withCredentials: true });
        setEvidenceElapsed(data.elapsed_seconds);
        if (data.progress) setEvidenceProgress(data.progress);
        if (data.ready) {
          clearInterval(interval);
          setEvidenceGenerating(false);
          setEvidenceReady(true);
          setEvidenceReadyVariant(data.redacted ? 'redacted' : 'full');
          toast.success(lang === 'es' ? 'Informe de evidencias generado' : 'Evidence report generated');
        } else if (data.error) {
          clearInterval(interval);
          setEvidenceGenerating(false);
          setEvidenceError(data.error);
          toast.error(lang === 'es' ? 'Error al generar informe' : 'Error generating report');
        }
      } catch {
        clearInterval(interval);
        setEvidenceGenerating(false);
      }
    }, 2000);
  };

  const downloadEvidence = async () => {
    try {
      // URS-F230: request the variant that matches the just-generated PDF
      const qs = evidenceReadyVariant === 'redacted' ? '?redacted=true' : '?redacted=false';
      const response = await axios.get(`${API_URL}/api/validation/evidence/download${qs}`, { withCredentials: true, responseType: 'blob' });
      // URS-F227: defensive check — if the backend returned JSON (e.g. 404
      // wrapped as blob by axios), surface the real message instead of the
      // generic "Error al descargar".
      const contentType = response.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const txt = await response.data.text();
        let detail = txt;
        try { detail = JSON.parse(txt)?.detail || txt; } catch (_) {}
        toast.error(detail);
        return;
      }
      if (!response.data || response.data.size === 0) {
        toast.error(lang === 'es' ? 'PDF vacio: el proceso fallo antes de escribir el fichero' : 'Empty PDF');
        return;
      }
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = evidenceReadyVariant === 'redacted' ? 'Informe_Evidencias_IQ_OQ_Redactado.pdf' : 'Informe_Evidencias_IQ_OQ.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(lang === 'es' ? 'PDF descargado' : 'PDF downloaded');
    } catch (err) {
      // Try to extract a JSON detail from a blob error response
      let detail = err.response?.data?.detail;
      if (!detail && err.response?.data instanceof Blob) {
        try {
          const txt = await err.response.data.text();
          detail = JSON.parse(txt)?.detail || txt;
        } catch (_) {}
      }
      toast.error(detail || (lang === 'es' ? 'Error al descargar' : 'Download error'));
    }
  };

  // URS-F312: estado por-card del toggle "Redactado" para los documentos que
  // lo soportan (URS, Matriz de Trazabilidad). El Informe de Evidencias tiene
  // su propio toggle aparte, ligado al proceso async de generacion.
  const [docRedacted, setDocRedacted] = useState({});

  const handleDownload = async (d) => {
    setDownloading(d.id);
    try {
      const isRedacted = !!(d.supportsRedacted && docRedacted[d.id]);
      const qs = d.supportsRedacted ? `?redacted=${isRedacted}` : '';
      const response = await axios.get(`${API_URL}${d.endpoint}${qs}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      // Anexar -Redactado al filename si aplica para distinguir versiones
      a.download = isRedacted ? d.filename.replace(/\.pdf$/i, '-Redactado.pdf') : d.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(lang === 'es' ? `${d.code} descargado${isRedacted ? ' (redactado)' : ''}` : `${d.code} downloaded${isRedacted ? ' (redacted)' : ''}`);
    } catch (err) {
      toast.error(errText(err, 'Error al generar PDF'));
    }
    setDownloading(null);
  };

  return (
    <div className="space-y-6" data-testid="validation-tab">
      <div className="bg-[#E8EDF0] border border-[#1f3e80]/20 rounded-sm p-4">
        <p className="text-sm text-[#1f3e80] font-medium">
          {lang === 'es'
            ? 'Documentacion de Validacion CSV (Computer System Validation)'
            : 'CSV (Computer System Validation) Documentation'}
        </p>
        <p className="text-xs text-[#1f3e80]/70 mt-1">
          {lang === 'es'
            ? 'Genera documentos PDF conformes a GAMP5, 21 CFR Part 11 y ALCOA+. Cada descarga queda registrada en el audit trail.'
            : 'Generate PDF documents compliant with GAMP5, 21 CFR Part 11 and ALCOA+. Each download is recorded in the audit trail.'}
        </p>
      </div>

      <div className="grid gap-4">
        {docs.map((d) => (
          <div key={d.id} className="bg-white border border-stone-200 rounded-sm p-5 flex items-start justify-between gap-4" data-testid={`validation-card-${d.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-mono bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">{d.code}</span>
                <span className="text-sm font-medium text-stone-900">{d.title}</span>
              </div>
              <p className="text-xs text-stone-500 leading-relaxed">{d.desc}</p>
              {d.supportsRedacted && (
                <div className="flex items-center gap-2 mt-2">
                  <Switch
                    checked={!!docRedacted[d.id]}
                    onCheckedChange={(v) => setDocRedacted(prev => ({ ...prev, [d.id]: v }))}
                    data-testid={`redacted-toggle-${d.id}`}
                    className="data-[state=checked]:bg-amber-600"
                  />
                  <span className="text-[11px] text-stone-600">
                    {lang === 'es'
                      ? (docRedacted[d.id] ? 'Modo Redactado (externo) - solo URS testeadas + 7 regulatorios' : 'Modo Completo (interno) - todas las URS')
                      : (docRedacted[d.id] ? 'Redacted mode (external) - tested URS + 7 regulatory only' : 'Complete mode (internal) - all URS')}
                  </span>
                </div>
              )}
            </div>
            <Button
              data-testid={`download-${d.id}-btn`}
              onClick={() => handleDownload(d)}
              disabled={downloading === d.id}
              className={`text-white rounded-sm gap-1.5 shrink-0 ${d.supportsRedacted && docRedacted[d.id] ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#1f3e80] hover:bg-[#2a4f8a]'}`}
            >
              <FileText size={14} />
              {downloading === d.id
                ? (lang === 'es' ? 'Generando...' : 'Generating...')
                : (d.supportsRedacted && docRedacted[d.id]
                    ? (lang === 'es' ? 'Descargar PDF redactado' : 'Download redacted PDF')
                    : (lang === 'es' ? 'Descargar PDF' : 'Download PDF'))}
            </Button>
          </div>
        ))}
      </div>

      {/* Cierre Formal de Fases (Plan de Validacion) */}
      <PhaseClosurePanel lang={lang} />

      {/* Enlaces publicos de Estado de Validacion */}
      <PublicValidationTokensPanel lang={lang} />

      {/* Informe de Evidencias IQ/OQ */}
      <div className="bg-[#EDE8F0] border border-[#6B4F8A]/20 rounded-sm p-4">
        <p className="text-sm text-[#4A3566] font-medium">
          {lang === 'es' ? 'Informe de Evidencias IQ/OQ' : 'IQ/OQ Evidence Report'}
        </p>
        <p className="text-xs text-[#4A3566]/70 mt-1">
          {lang === 'es'
            ? 'Ejecuta tests automatizados Playwright contra cada requisito URS, captura screenshots como evidencia objetiva y genera un PDF completo para auditoria. La generacion tarda aproximadamente 1-2 minutos.'
            : 'Runs automated Playwright tests against each URS requirement, captures screenshots as objective evidence and generates a complete PDF for audit. Generation takes approximately 1-2 minutes.'}
        </p>
      </div>
      <div className="grid gap-4">
        <div className="bg-white border border-stone-200 rounded-sm p-5 flex items-start justify-between gap-4" data-testid="validation-card-evidence">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-mono bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded border border-purple-200">{evidenceDoc.code}</span>
              <span className="text-sm font-medium text-stone-900">{evidenceDoc.title}</span>
            </div>
            <p className="text-xs text-stone-500 leading-relaxed">{evidenceDoc.description}</p>
            {evidenceGenerating && (
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs text-purple-600 font-medium">
                      {lang === 'es' ? 'Ejecutando tests y capturando evidencias' : 'Running tests and capturing evidence'}
                      {evidenceElapsed !== null && ` · ${Math.floor(evidenceElapsed / 60)}m ${evidenceElapsed % 60}s`}
                    </span>
                  </div>
                  {evidenceElapsed !== null && evidenceElapsed > 600 && (
                    <button
                      data-testid="evidence-force-reset-btn"
                      onClick={resetEvidence}
                      className="text-[11px] px-2 py-0.5 rounded bg-red-50 border border-red-200 text-red-700 hover:bg-red-100"
                    >
                      {lang === 'es' ? 'Forzar reset (lleva colgado >10min)' : 'Force reset (>10min hung)'}
                    </button>
                  )}
                </div>
                {/* URS-F227: live progress bar */}
                <div data-testid="evidence-progress-bar" className="w-full">
                  <div className="flex items-center justify-between text-[11px] text-stone-600 mb-1">
                    <span className="font-mono">
                      {evidenceProgress.total > 0
                        ? `${evidenceProgress.current} / ${evidenceProgress.total} ${lang === 'es' ? 'pruebas' : 'tests'}`
                        : (lang === 'es' ? 'Inicializando Playwright...' : 'Initializing Playwright...')}
                    </span>
                    <span className="font-mono font-semibold text-purple-700">{evidenceProgress.percent || 0}%</span>
                  </div>
                  <div className="w-full h-2 bg-stone-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-500 ease-out"
                      style={{ width: `${evidenceProgress.percent || 0}%` }}
                    />
                  </div>
                  {evidenceProgress.urs && (
                    <div className="mt-1 text-[10px] text-stone-500 truncate">
                      {lang === 'es' ? 'Ultimo test ejecutado:' : 'Last test executed:'}{' '}
                      <span className="font-mono text-stone-700">{evidenceProgress.urs}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {evidenceError && (
              <p className="mt-2 text-xs text-red-600">{lang === 'es' ? 'Error: ' : 'Error: '}{evidenceError}</p>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0 items-end">
            {/* URS-F230: redaction toggle */}
            <label
              className={`flex items-center gap-2 text-xs select-none cursor-pointer px-2 py-1 rounded-sm border ${evidenceRedacted ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-stone-50 border-stone-200 text-stone-600 hover:bg-stone-100'}`}
              data-testid="evidence-redacted-toggle-label"
            >
              <input
                type="checkbox"
                checked={evidenceRedacted}
                onChange={(e) => setEvidenceRedacted(e.target.checked)}
                disabled={evidenceGenerating}
                data-testid="evidence-redacted-toggle"
                className="accent-amber-600 cursor-pointer disabled:cursor-not-allowed"
              />
              <span>
                {lang === 'es'
                  ? 'Redactar datos personales (GDPR)'
                  : 'Redact personal data (GDPR)'}
              </span>
            </label>
            <div className="flex gap-2">
              {evidenceReady && (
                <Button
                  data-testid="download-evidence-pdf-btn"
                  onClick={downloadEvidence}
                  className={`text-white rounded-sm gap-1.5 ${evidenceReadyVariant === 'redacted' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#16a34a] hover:bg-[#15803d]'}`}
                >
                  <FileText size={14} />
                  {lang === 'es'
                    ? (evidenceReadyVariant === 'redacted' ? 'Descargar PDF redactado' : 'Descargar PDF')
                    : (evidenceReadyVariant === 'redacted' ? 'Download redacted PDF' : 'Download PDF')}
                </Button>
              )}
              <Button
                data-testid="download-evidence-btn"
                onClick={startEvidence}
                disabled={evidenceGenerating}
                className={`text-white rounded-sm gap-1.5 ${evidenceRedacted ? 'bg-amber-700 hover:bg-amber-800' : 'bg-[#6B4F8A] hover:bg-[#553D6E]'}`}
              >
                <FileText size={14} />
                {evidenceGenerating
                  ? (lang === 'es' ? 'Generando...' : 'Generating...')
                  : (lang === 'es'
                      ? (evidenceRedacted ? 'Generar Informe Redactado' : 'Generar Informe')
                      : (evidenceRedacted ? 'Generate Redacted Report' : 'Generate Report'))}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* SOPs Section */}
      <div className="bg-[#F0EDE8] border border-[#8B7355]/20 rounded-sm p-4">
        <p className="text-sm text-[#5C4A32] font-medium">
          {lang === 'es'
            ? 'Procedimientos Normalizados de Trabajo (SOPs / PNTs)'
            : 'Standard Operating Procedures (SOPs)'}
        </p>
        <p className="text-xs text-[#5C4A32]/70 mt-1">
          {lang === 'es'
            ? 'Procedimientos que describen como operar cada modulo del sistema. Se actualizan automaticamente con cada cambio funcional.'
            : 'Procedures describing how to operate each system module. Automatically updated with each functional change.'}
        </p>
      </div>

      <div className="grid gap-4">
        {sops.map((d) => (
          <div key={d.id} className="bg-white border border-stone-200 rounded-sm p-5 flex items-start justify-between gap-4" data-testid={`validation-card-${d.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-mono bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200">{d.code}</span>
                <span className="text-sm font-medium text-stone-900">{d.title}</span>
              </div>
              <p className="text-xs text-stone-500 leading-relaxed">{d.desc}</p>
            </div>
            <Button
              data-testid={`download-${d.id}-btn`}
              onClick={() => handleDownload(d)}
              disabled={downloading === d.id}
              className="bg-[#8B7355] hover:bg-[#6E5A43] text-white rounded-sm gap-1.5 shrink-0"
            >
              <FileText size={14} />
              {downloading === d.id
                ? (lang === 'es' ? 'Generando...' : 'Generating...')
                : (lang === 'es' ? 'Descargar PDF' : 'Download PDF')}
            </Button>
          </div>
        ))}
      </div>

      {/* Manual de Usuario Section */}
      <div className="bg-[#E8F0E8] border border-[#2d6a4f]/20 rounded-sm p-4">
        <p className="text-sm text-[#2d6a4f] font-medium">
          {lang === 'es' ? 'Manual de Usuario' : 'User Manual'}
        </p>
        <p className="text-xs text-[#2d6a4f]/70 mt-1">
          {lang === 'es'
            ? 'Guia completa de uso del sistema con instrucciones paso a paso y capturas de pantalla. Se actualiza automaticamente con cada cambio.'
            : 'Complete system user guide with step-by-step instructions and screenshots. Automatically updated with each change.'}
        </p>
      </div>
      <div className="grid gap-4">
        <div className="bg-white border border-stone-200 rounded-sm p-5 flex items-start justify-between gap-4" data-testid="validation-card-manual">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-mono bg-stone-100 text-stone-500 px-1.5 py-0.5 rounded">{manualDoc.code}</span>
              <span className="text-sm font-medium text-stone-900">{manualDoc.title}</span>
            </div>
            <p className="text-xs text-stone-500 leading-relaxed">{manualDoc.description}</p>
          </div>
          <Button
            data-testid="download-manual-btn"
            onClick={() => handleDownload(manualDoc)}
            disabled={downloading === 'manual'}
            className="bg-[#2d6a4f] hover:bg-[#1b4332] text-white rounded-sm gap-1.5 shrink-0"
          >
            <FileText size={14} />
            {downloading === 'manual'
              ? (lang === 'es' ? 'Generando...' : 'Generating...')
              : (lang === 'es' ? 'Descargar PDF' : 'Download PDF')}
          </Button>
        </div>
      </div>
    </div>
  );
}
